import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, LinearProgress, Typography, Button, Snackbar, CircularProgress, Dialog, useMediaQuery } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import DefaultScreen from '../DefaultScreen';
import useRedirect from '../../helpers/Routes/Redirect';
import { sitePrefix } from '../../config';
import { PulseemFeatures } from '../../model/PulseemFields/Fields';
import {
    getMapping, setCampaignContext, setTokenMapping, setBusinessColumn,
    setMapping, fillAndSummarize, setEmailSendSettingsWrapped, loadSourceColumns,
    getSendSummaryWrapped, getEmailSendSettingsWrapped, selectUnmappedTokens, selectHasMappingWork,
    setChannel, getMappingSuggestions, memorySuggestionsKey, MemorySuggestionsArg,
    leaveSend, readMappingRow, selectMappingRowFor, MappingRowState, clearMappingRow,
} from '../../redux/reducers/smartSendSlice';
import {
    armConfirmReset, clearConfirmReset, isConfirmResetArmed, confirmResetTarget, markChangeSourceTrip, takeConfirmEntry,
} from './confirmReset';
import { CHANNELS, eSendChannel, SmartSendTokenInfo, SaveMappingRequest } from '../../Models/DataSources/SmartSend';
import { eDataSourceStatus } from '../../Models/DataSources/DataSource';
import InlineBanner from './components/InlineBanner';
import SourcePicker from './components/SourcePicker';
import TokenMappingTable, { TokenSuggestion } from './components/TokenMappingTable';
import BusinessColumnsPicker from './components/BusinessColumnsPicker';
import StaleVersionBanner from './components/StaleVersionBanner';
import MappingMismatchBanner from './components/MappingMismatchBanner';
import UnmappedTokensWarning from './components/UnmappedTokensWarning';
import SmartSendPreviewDialog from './components/SmartSendPreviewDialog';
import SendSummaryDialog from './components/SendSummaryDialog';
import TestSendDialog from './components/TestSendDialog';
import ChannelTabs, { WaTabKey, TabRemoveAction } from './whatsapp/ChannelTabs';
// 09-channel-units — no channel tiles on this page any more: the channels ARE the page's units,
// chosen on the picker. A "both" page offers "הסר" beside each tab instead (ChannelTabs).
import WhatsAppMappingTab from './whatsapp/WhatsAppMappingTab';
import WhatsAppPhonePreview from './whatsapp/WhatsAppPhonePreview';
import WhatsAppTestSendDialog from './whatsapp/WhatsAppTestSendDialog';
import ActionBar from './whatsapp/ActionBar';
import useWaAutosave from './whatsapp/useWaAutosave';
import useWaErrorText from './whatsapp/errorText';
import { memoryColumnName, memoryRowForToken, usableMemoryColumnId } from './whatsapp/waTokens';
import {
    selectWaActive, selectWaAllowed, selectWaCanSend, selectEmailLocked, selectWaOnlyIntent,
    selectShowChannelTabs, WaRootState,
} from './whatsapp/selectors';
import {
    getWaAvailability, getWaMapping, getWaSummary, getWaTemplates, seedWaFromUrl,
    setWithWhatsApp, setWaUnit, WaUnitMode, setWaSaveState, clearWaUsesUnitErrors,
} from '../../redux/reducers/smartSendWhatsAppSlice';

// Smart-Send screen (מסך השליחה החכמה). Wizard: SourcePicker →
// TokenMappingTable → BusinessColumnsPicker → Preview → Summary → Send (§11/§13).
// Route: `${sitePrefix}Campaigns/SmartSend/:id` (+ optional ?dataSourceId= from entry A).
const ERR_KEY: { [k: string]: string } = {
    EDIT_BLOCKED_DURING_SEND: 'editBlockedDuringSend', VIEW_ONLY: 'viewOnlyBlocked',
    GROUP_MERGE_LIMIT: 'groupMergeLimit', DATA_INCORRECT: 'dataIncorrect',
    CHANNEL_NOT_SUPPORTED: 'channelNotSupported', NOT_FOUND: 'notFound',
    // 12-fix-wave 7.8 — the new codes of contract §3 and 5.5.
    SOURCE_RESET_CONFIRM_REQUIRED: 'sourceResetConfirmRequired', SEND_ALREADY_SENT: 'sendAlreadySent',
    UNIT_IN_OTHER_SEND: 'unitInOtherSend', UNIT_MAP_NOT_IN_VERSION: 'unitMapNotInVersion',
    SEND_USES_UNIT: 'sendUsesUnit', NOT_A_BOTH_SEND: 'notABothSend', WA_PART_OF_BOTH: 'waPartOfBoth',
};

const SmartSendUnitsPage = ({ classes }: any) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const Redirect = useRedirect();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const dataSourceId = searchParams.get('dataSourceId');
    // Entry A ?dataSourceId — a garbage param must land as null, never as a bogus id.
    // A NaN guard alone is NOT enough: `Number('')` is 0 (and `Number(' ')` is 0 too), so
    // `?dataSourceId=` — which the picker emits whenever its source chip is cleared, and
    // which any hand-edited URL can produce — would pass the guard and store 0. SourcePicker
    // then treats a stored id as "an id to preselect". Reject anything <= 0 as well, so the
    // only values that reach the store are real, positive source ids.
    // Hoisted out of the load effect because the back-to-picker link in the header has to hand
    // the SAME sanitised id back to the picker.
    const dsNum = dataSourceId != null ? Number(dataSourceId) : NaN;
    const parsedDsId = !Number.isNaN(dsNum) && dsNum > 0 ? dsNum : null;

    const campaignId = Number(id);

    // 🔴🔴 THE CHANNEL IS A FACT OF THE ROUTE, NOT A LEFTOVER OF THE SESSION.
    //
    // This is the revisit the comment further down demanded ("MUST be revisited when SMS is
    // wired"). WhatsApp is now wired, so the condition that made the old behaviour harmless —
    // "EMAIL is the only enabled channel, so the fallback and the sticky value are the same
    // value" — has stopped being true, and the bug that comment describes is live:
    //   `clearSmartSend` has ZERO dispatch sites, so `selectedChannel` is written once by the
    //   picker and then survives the whole SPA session. Choose WhatsApp-only, then open an
    //   E-MAIL campaign by a direct link (campaign-row icon, BEE editor, pasted URL) and this
    //   screen would fetch THAT campaign's mapping on channel 3 — and a save would then write
    //   a channel-3 row keyed by an e-mail CampaignID.
    //
    // So the channel is derived here, per campaign, from the URL: `?channel=3` is what the
    // picker sets when it hands over a WhatsApp container id, and its ABSENCE means e-mail.
    // Absence is not "whatever was chosen last" — it is a positive answer, and it is the one
    // that keeps every pre-existing entry point behaving exactly as it did before WhatsApp.
    // ⚠️ An unknown or disabled value falls back to EMAIL rather than throwing: this is a route
    //    an operator can reach with a hand-edited address bar, and the safe reading of a channel
    //    nobody wired is the one channel that is always wired.
    const urlChannel = searchParams.get('channel');
    useEffect(() => {
        const asked = urlChannel != null ? Number(urlChannel) : eSendChannel.EMAIL;
        const known = CHANNELS.some((c) => c.channel === asked && c.enabled);
        dispatch(setChannel(known ? asked : eSendChannel.EMAIL));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, urlChannel]);

    // ══ 09-channel-units — WHICH UNITS THIS PAGE CARRIES, FROM THE URL AND NOTHING ELSE ══════════
    // Every channel is its own unit: its own container and its own mapping row (06-docs §1.1).
    //   ?channel=3 ⇒ this page IS the WhatsApp unit — `campaignId` is its WACampaignID;
    //   ?wa={id}   ⇒ this page is the e-mail unit, sent together with the WhatsApp unit {id};
    //   neither    ⇒ the e-mail unit alone.
    // Every WhatsApp request on this screen goes to `waUnitId` on channel 3, and no WhatsApp request
    // ever carries the e-mail campaign's id. The picker creates the WhatsApp unit — container AND
    // mapping row — before navigating here, so this screen only ever loads units that exist.
    const isWaPage = Number(urlChannel) === 3;
    // 12-fix-wave 7.4(a) — THE PARTNER COMES ONLY FROM THE DB (D5). The page's own container is the
    // URL's (`:id` + `?channel=`); the send's other channel is the row's, as the last GetMapping of
    // THIS page read it (`RowWACampaignID` on an e-mail page, `RowCampaignID` on a unit's page). A `wa`
    // still present in an old URL is ignored.
    const pageChannel: number = isWaPage ? 3 : 1;
    const mappingRow: MappingRowState | null = useSelector((s: any) => selectMappingRowFor(s, campaignId, pageChannel));
    // A WACampaignID is a positive int IDENTITY: `1.5`, `0x10`, `Infinity` or anything past int32 is
    // not a unit, and the page is then the e-mail unit alone rather than a "both" page whose every
    // WhatsApp request the server refuses.
    const isUnitId = (n: number) => Number.isInteger(n) && n > 0 && n <= 2147483647;
    const rowWa: number | null = !isWaPage ? (mappingRow?.rowWaCampaignId ?? null) : null;
    const waUnitId: number | null = isWaPage
        ? (isUnitId(campaignId) ? campaignId : null)
        : (rowWa != null && isUnitId(rowWa) ? rowWa : null);
    const unitMode: WaUnitMode = isWaPage ? 'whatsapp' : (waUnitId != null ? 'both' : 'email');
    // On a unit's own page: the e-mail campaign its row now holds, if any (it is part of a "both" send).
    const rowEmailOfWaPage: number | null = isWaPage ? (mappingRow?.rowCampaignId ?? null) : null;
    const { accountFeatures } = useSelector((state: any) => state.common);
    const isRTL = useSelector((state: any) => state.core && state.core.isRTL);
    const smartSend = useSelector((state: any) => state.smartSend);
    const unmapped = useSelector(selectUnmappedTokens);
    // The campaign identity shown in the header. It comes from the newsletter summary the reused
    // pipeline hydrates (getSendSummaryWrapped, dispatched from openSummary) — deliberately NO
    // extra fetch is added here, so until that lands the line is simply absent rather than a
    // placeholder. `newsletterSendSummary` is session-sticky (newsletterSlice.js:263 seeds it to
    // [] and nothing ever clears it), so a summary left over from a DIFFERENT campaign would
    // otherwise label this screen with the wrong name — the whole point of the label is that the
    // user can trust it, so it renders only when the hydrated CampaignID is this campaign's.
    const sendSummary = useSelector((state: any) => state.newsletter && state.newsletter.newsletterSendSummary);
    // QA BUG-35 — and, from entry on, the name the page already has without a request: the campaign lists the picker
    // loaded (newsletter MainList/ChildList, rows keyed by CampaignID), matched on THIS campaign's id. Without them the
    // header shows the id from the URL alone.
    const listedName: string | null = useSelector((state: any) => {
        if (isWaPage) return null;
        const nl = state.newsletter ?? {};
        const rowOf = (rows: any) => (Array.isArray(rows) ? rows.find((r: any) => r && r.CampaignID === campaignId) : null);
        const r = rowOf(nl.newslettersParentCampaigns) ?? rowOf(nl.newslettersChildCampaigns);
        return r && r.Name ? String(r.Name) : null;
    });
    // (Not on the WhatsApp unit's own page: its id is a WACampaignID, and the summary's CampaignID is an e-mail id.)
    const campaignName: string | null = (!isWaPage && sendSummary && sendSummary.CampaignID === campaignId
        ? (sendSummary.CampaignName || null)
        : null)
        ?? listedName;

    const currentSourceId = smartSend.dataSource?.DataSourceID ?? smartSend.dataSourceId ?? null;
    const hasColumns = smartSend.columns.length > 0;

    // ── what the SERVER holds, and whether the table still shows it ─────────────────────────────
    // `savedMapping` is five scalars written only by the server round trips; it deliberately
    // survives selectSource (smartSendSlice.ts, see the comment there). The campaignId equality is
    // required, not defensive: clearSmartSend has zero dispatch sites, so this slice outlives a
    // campaign-to-campaign navigation and a descriptor left by the PREVIOUS campaign must never
    // label this one. Same read-site pattern as campaignName above.
    const savedRaw = smartSend.savedMapping;
    const saved = savedRaw && savedRaw.campaignId === campaignId ? savedRaw : null;
    // Gated on workingIsFromServer, NEVER on `saved.dataSourceId !== currentSourceId`: the id
    // comparison goes false on A→B→A while tokenMap is still empty, which is the exact reported
    // complaint — no offer to restore, and a header confidently asserting "14 מתוך 17" over an
    // empty table.
    const showRestore = saved != null && !smartSend.workingIsFromServer;
    const hasMappingWork = useSelector(selectHasMappingWork);

    const [confirmUnmapped, setConfirmUnmapped] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    // Set by SendSummaryDialog's onSent (a 200/201 from sendSmart) and NOT acted on until the
    // user closes the dialog — see the SendSummaryDialog block at the bottom of this file.
    const [sent, setSent] = useState(false);
    // The working tokenMap differs from what is persisted. There is no dirty flag in the slice
    // and none can be derived from it: setMapping.fulfilled does not write the new mapping back
    // onto `tokens`, so comparing tokenMap to `tokens[].MappedColumnID` would keep claiming
    // "unsaved" straight after a successful save. Tracked here instead, at the one place in this
    // screen that edits the map.
    const [dirty, setDirty] = useState(false);
    // The WhatsApp tab keeps its own unsaved state, in the slice — the e-mail  above says
    // nothing about it, and on a WhatsApp-only send it is false for the whole session.
    const waDirty = useSelector((s: any) => s.smartSendWa.waDirty);
    const suppressEmailDirty = useSelector((s: any) => s.smartSendWa.suppressEmailDirty);
    // item 2: the synthetic group id returned by saveMapping, held so the confirmed Send inside
    // SendSummaryDialog can attach it (attachGroup) — the attach is deferred out of openSummary.
    const [pendingGid, setPendingGid] = useState<number | null>(null);
    // item 3: three-way exit confirm (Save & exit / Exit without saving / Cancel).
    const [exitOpen, setExitOpen] = useState(false);
    // QA BUG-10 (B-11 / L-06) — both autosaves are HELD while the exit dialog is up: no new e-mail
    // timer (the debounce effect), no trailing e-mail re-fire (`runAutoSave` reads this ref), and the
    // WhatsApp hook's `paused`. The dialog's own buttons decide: save once, discard, or resume on cancel.
    const exitOpenRef = useRef(false);
    exitOpenRef.current = exitOpen;
    const [exitSaveFailed, setExitSaveFailed] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; ok: boolean; msg: string }>({ open: false, ok: true, msg: '' });
    // Restore-from-server lifecycle. `restoring` also keeps the body mounted through the reload —
    // see the restoreOverlay guard in renderBody, without which this feature performs the very
    // disappearance it exists to prevent.
    const [restoring, setRestoring] = useState(false);
    const [restoreFailed, setRestoreFailed] = useState(false);
    // WHICH source the last failed autosave belonged to. The Retry button rebuilds the request from
    // FRESH state, so after a source switch it would post the new source's snapshot as the "retry"
    // of the old source's save.
    const [failedSaveSourceId, setFailedSaveSourceId] = useState<number | null>(null);
    const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);

    // ── SmartSend part B (WhatsApp) ─────────────────────────────────────────────────────────
    // Everything below is inert unless `AllowWhatsAppToAgent` AND `Availability.Enabled`.
    // With either off, `waActive` is false, no WhatsApp/* call is made, and every branch in
    // this file falls back to exactly the render it had before (14-W3 §2).
    const waAllowed = useSelector((st: WaRootState) => selectWaAllowed(st));
    const waActive = useSelector((st: WaRootState) => selectWaActive(st));
    const waCanSend = useSelector((st: WaRootState) => selectWaCanSend(st));
    // 🔴 INVARIANT 24. `HasLeg` is part of the predicate word for word — it must be the SAME
    // predicate as the "send to all" gate, or the gap `waActive ∧ ¬HasLeg ∧ e-mail already
    // out` opens: the tab locked, the button live, and `openSummary` handed `gid = null` —
    // which sends the campaign WITHOUT its synthetic group.
    const emailLocked = useSelector((st: WaRootState) => selectEmailLocked(st));

    // ══ 09-channel-units — "הסר" MOVES BETWEEN UNITS; IT WRITES NOTHING AND DELETES NOTHING ═══════
    // The page's channels ARE its units (`unitMode`, from the URL), so there is no channel state to
    // write. On a "both" page each tab offers "הסר" (ChannelTabs), and it changes WHICH UNITS THIS
    // SEND CARRIES — symmetrically:
    //   · WhatsApp "הסר" ⇒ continue on the e-mail unit's own page;
    //   · e-mail "הסר" ⇒ continue on the WhatsApp unit's own page.
    // The unit left behind stays exactly as it is. Not sending it now deletes nothing, and that is
    // load-bearing for the WhatsApp side: the same choice is the same unit (POST WhatsApp/Container
    // hands an unsent unit back), so that unit is also the saved mapping of that choice — and may be
    // open in another tab. Deleting it here would take both with it. A draft is deleted from the
    // manage list. (2026-09-28; until then the WhatsApp tile OFF deleted the unit.)
    // ADDING a channel is the picker's job, because that is where a WhatsApp unit is created.
    // Selected as SCALARS: a selector returning a fresh object re-renders on every dispatch.
    const waEnabledForBlock = useSelector((st: WaRootState) => !!st.smartSendWa?.waAvailability?.Enabled);
    // QA BUG-35 — the "both" page's WhatsApp template, for the header line: the unit's own card, once THIS unit's GetMapping
    // has seeded it (never a card left from an earlier choice).
    const waTemplateName: string | null = useSelector((st: any) => {
        const w = st.smartSendWa;
        if (!w || waUnitId == null || w.mappingCampaignId !== waUnitId || !w.waMapping?.HasLeg) return null;
        return w.waTemplateCard?.name ?? null;
    });
    // QA BUG-35 — "the account's WhatsApp is off" only when that is KNOWN (no role, or Availability answered not Enabled);
    // while Availability is on its way, or failed, the banner does not claim it.
    const waAvailabilityKnownOff = useSelector((st: any) => !selectWaAllowed(st)
        || (st.smartSendWa?.availabilityStatus === 'succeeded' && !st.smartSendWa?.waAvailability?.Enabled));
    const { waErrorText } = useWaErrorText();

    const dsQuery = parsedDsId != null ? `dataSourceId=${parsedDsId}` : '';
    const emailUnitUrl = `${sitePrefix}Campaigns/SmartSend/${campaignId}${dsQuery ? `?${dsQuery}` : ''}`;
    const waUnitUrl = waUnitId != null
        ? `${sitePrefix}Campaigns/SmartSend/${waUnitId}?channel=3&tab=whatsapp${dsQuery ? `&${dsQuery}` : ''}`
        : null;

    // WHERE "LEAVE" GOES. The back link goes to the picker; the e-mail switch goes to the WhatsApp
    // unit's own page; "change source" goes to the picker without the source. All three pass the
    // SAME unsaved-work gate (`leaveWithGate` below), so moving between units can never drop an
    // armed autosave any more than leaving can. Read by `doExit`; null ⇒ the picker. Another unit's
    // page starts from its URL alone: the page instance is keyed on its units (`SmartSendScreen`
    // at the bottom of this file).
    const exitTarget = useRef<string | null>(null);
    // 12-fix-wave 7.4(b) — set by «הסר»: the exit first splits the send (POST Leave), then navigates.
    const pendingLeave = useRef(false);
    // 12-fix-wave 7.4(b) — true while POST Leave is on the wire.
    const leavingRef = useRef(false);
    // 12-fix-wave 7.4(d) — set by «אפס והמשך» at «החלף מקור»: `doExit` arms the reset confirmation.
    const armResetOnExit = useRef(false);

    // 12-fix-wave 7.4(b) / §1.9 — «הסר» only while BOTH channels are editable: the unit's
    // `WaCampaignStatus` is 1 and `EmailCampaignStatus` is 1 or NULL. Both come from the unit's own
    // GetMapping (channel 3); the e-mail's status there is 4.8(c)'s. Unknown (not read yet) ⇒ disabled.
    const waUnitStatus: number | null = useSelector((s: any) => s.smartSendWa.waMapping?.WaCampaignStatus ?? null);
    const waEmailStatus: number | null = useSelector((s: any) => s.smartSendWa.waMapping?.EmailCampaignStatus ?? null);
    const bothEditable = waUnitStatus === 1 && (waEmailStatus ?? 1) === 1;
    // 12-fix-wave 7.4(d) / §1.9 — a CLOSED send: the page's `EmailCampaignStatus ?? 1` is not 1, or the
    // present unit's `WaCampaignStatus` is not 1 (the same two fields, from the unit's GetMapping). An
    // e-mail-only page reads no status here; the server refuses its saves (-6 / -41).
    const sendIsClosed = (waEmailStatus ?? 1) !== 1
        || (waUnitId != null && waUnitStatus != null && waUnitStatus !== 1);
    // QA BUG-06 (C-06) — the hover reason (the D1 text) of the controls a closed send disables; null while the send is open.
    const sendClosedReason: string | null = sendIsClosed ? t('DataSources.send.errors.sendClosed') : null;

    // 12-fix-wave 7.4(b) — the hover reason only when the statuses are known and say "closed"; while the
    // unit's mapping is loading (or failed) the action is disabled without a claim.
    const removeReason: string | null = waUnitStatus != null && !bothEditable ? t('DataSources.send.errors.sendClosed') : null;

    // The e-mail "הסר" on a "both" page. Once the mail is out the send is decided, so it is refused,
    // with the reason on hover.
    // 12-fix-wave 7.4(b) — POST Leave first (in `doExit`), then the WhatsApp unit's own page.
    const removeEmailAction: TabRemoveAction | null = (waAllowed && waEnabledForBlock && unitMode === 'both' && waUnitUrl != null)
        ? {
            disabled: !bothEditable,
            reason: removeReason,
            onRemove: () => {
                exitTarget.current = waUnitUrl;
                pendingLeave.current = true;
                armResetOnExit.current = false;
                leaveWithGate();
            },
        }
        : null;

    // The WhatsApp "הסר" on a "both" page. Always offered: it deletes nothing (see above), so it needs
    // no confirmation beyond the unsaved-work gate — and it is exactly the way out when the WhatsApp
    // cannot go (the add-on not Ready; the WhatsApp tab then says why, with the templates link).
    // 12-fix-wave 7.4(b) — POST Leave first (in `doExit`), then the e-mail page; offered only while both
    // channels are editable.
    const removeWaAction: TabRemoveAction | null = (waAllowed && waEnabledForBlock && unitMode === 'both' && waUnitId != null)
        ? {
            disabled: !bothEditable,
            reason: removeReason,
            onRemove: () => {
                exitTarget.current = emailUnitUrl;
                pendingLeave.current = true;
                armResetOnExit.current = false;
                leaveWithGate();
            },
        }
        : null;

    // QA BUG-35 (B) — EVERY "both" e-mail page says so, whatever door it was opened from (the picker, R2, a link): the
    // send's row holds a WhatsApp unit, and «שלח לכולם» sends both (the server takes the unit from the row). Its «הסר» is
    // the WhatsApp tab's own when that is offered (WhatsApp on); otherwise the SAME POST Leave through the SAME gate
    // (`doExit` → `runLeave`): with the account's WhatsApp off the page has no tabs and no «הסר», yet the unit still holds
    // the whole send (the summary waits for it and the server refuses) — this is the way out. Leave needs only the send
    // permission, and deletes nothing. Not on a closed send (nothing more goes out from it).
    const bannerRemove: TabRemoveAction | null = !isWaPage && unitMode === 'both' && waUnitId != null && !sendIsClosed
        ? (removeWaAction ?? {
            disabled: false,
            reason: null,
            onRemove: () => {
                exitTarget.current = emailUnitUrl;
                pendingLeave.current = true;
                armResetOnExit.current = false;
                leaveWithGate();
            },
        })
        : null;

    // ── the channel state (§6). 09-channel-units: it is the page's UNITS, read off the URL
    // (`unitMode` above, stored by `setWaUnit`) — never a leg's `SuppressEmail`. Every WhatsApp
    // selector answers from `unitMode` when it is set (selectors.ts `unitMode(s)`), and
    // `selectedChannel` follows `?channel=` (the effect at the top of this component: 3 on the
    // WhatsApp unit's own page, 1 otherwise). `waOnly` here is "this page IS the WhatsApp unit".
    const waOnly = useSelector((st: WaRootState) => selectWaOnlyIntent(st));
    const showTabs = useSelector((st: WaRootState) => selectShowChannelTabs(st));
    const waAvailabilityStatus = useSelector((st: any) => st.smartSendWa.availabilityStatus);
    const waTemplatesStatus = useSelector((st: any) => st.smartSendWa.templatesStatus);
    const [waTestOpen, setWaTestOpen] = useState(false);
    // Order of truth for the visible tab: URL first (the picker's Continue and the WhatsApp
    // unit's own page URL carry `?tab=whatsapp`), then e-mail.
    const [activeTab, setActiveTab] = useState<WaTabKey>(
        searchParams.get('tab') === 'whatsapp' ? 'whatsapp' : 'email');
    // 12-fix-wave 7.4(d) — autosave is held while the reset dialog is up (`resetHold`), and for good once
    // a save answered SEND_ALREADY_SENT (`closedHold`): the send is closed and no save can succeed.
    const [resetHold, setResetHold] = useState(false);
    const [closedHold, setClosedHold] = useState(false);
    // 09-channel-units — the WhatsApp UNIT's own id on channel 3; 0 (no unit) posts nothing.
    // 12-fix-wave 7.4(d) — `pageId` keys the reset confirmation; `paused` holds its debounce.
    const waAutosave = useWaAutosave(waUnitId ?? 0, 3, { pageChannel, pageId: campaignId, paused: resetHold || closedHold || exitOpen });

    // 🔴 "IS THERE WHATSAPP WORK THAT A SAVE WOULD ACTUALLY PERSIST", ONE DEFINITION, TWO USERS.
    // Read by the exit gate (:862) and by `openSummary` (:754) — the two points of no return on
    // this screen. Declared HERE, above both, so neither reads it before it is initialised.
    //
    // `dirty` is the E-MAIL mapping's flag: `markEdited` (:415) is its only writer and both of
    // its call sites are inside the e-mail panel, so on a WhatsApp-only send it is false for the
    // whole session. Anything that asks "is there unsaved work" through `dirty` alone is asking
    // about the one channel that is not being used.
    //
    // ⚠️ `canWrite` IS PART OF THE PREDICATE, and that is where this deliberately stops being a
    // copy of the beforeunload gate at :457. That gate can afford the raw disjunction — it only
    // arms the browser's own warning. These two decide whether to OFFER A SAVE, and offering one
    // in a state where `saveWaMapping` returns without dispatching (useWaAutosave.ts:74) is the
    // same false promise in a new place. Two states reach that: the money lock (1b) and
    // `EmailCampaignStatus = 2`. And `suppressEmailDirty` can be up with no operator action at
    // all — `setSuppressEmail` raises it unconditionally (09-channel-units removed the `?waOnly=1`
    // seed that used to do so on every channel-3 mount, but the reducer still can) — so without
    // `canWrite` a locked screen would open the exit dialog over work nothing could save.
    //
    // 🔴 AND `waTemplateId` IS PART OF IT TOO — `canWrite` DOES NOT COVER THAT REFUSAL.
    // `persist` tests them in sequence and they are NOT the same test: `!canWrite` returns
    // `{ ok: TRUE }` (useWaAutosave.ts:74) while `!templateId` returns `{ ok: FALSE }`
    // (useWaAutosave.ts:78). A predicate carrying only `canWrite` therefore answers "there is
    // work to save" for a state whose save can never succeed, and the callers below turn that
    // into a screen the operator cannot leave and a send that never opens its dialog.
    // The state is ordinary, not exotic: `setWithWhatsApp(false)` — un-ticking the WhatsApp tile
    // on a two-channel campaign — nulls `whatsAppTemplateId` and raises `suppressEmailDirty` in
    // ONE reducer (slice:577-591). Nothing then clears the marker: `setWaMapping.fulfilled` is
    // the only clearer (slice:902) and it is unreachable without a template, and the 750 ms
    // debounce bails on the same field (useWaAutosave.ts:147). It would stick until a reload.
    const waTemplateId = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId);
    const waNeedsSave = waAutosave.canWrite && !!waTemplateId && (waDirty || suppressEmailDirty);

    // `Availability` is always the first WhatsApp call, and it never 4xxs (contract §6).
    useEffect(() => {
        if (waAllowed && waAvailabilityStatus === 'idle') dispatch(getWaAvailability());
    }, [dispatch, waAllowed, waAvailabilityStatus]);

    // 09-channel-units — THE WHATSAPP UNIT IS READ BY ITS OWN ID ON CHANNEL 3, and only when the page
    // carries one. An e-mail-only page reads no WhatsApp at all: whatever leg an older campaign may
    // still carry is not a unit of this send.
    // ⚠️ No `dbo.CampaignChannel` read either (`GET Channels`): the page's channels are its units,
    // named by the URL, so there is no channel state to load.
    useEffect(() => {
        if (waUnitId == null) return;
        if (waAllowed && waAvailabilityStatus === 'succeeded') dispatch(getWaMapping({ campaignId: waUnitId, channel: 3 }));
    }, [dispatch, waUnitId, waAllowed, waAvailabilityStatus]);

    // The template list feeds the card and the phone preview's body text.
    useEffect(() => {
        if (waActive && waTemplatesStatus === 'idle') dispatch(getWaTemplates());
    }, [dispatch, waActive, waTemplatesStatus]);

    // ══ C-35 · THE URL IS THE ONLY CARRIER BETWEEN THE TWO SCREENS, AND IT MUST NOT WAIT ══
    //
    // 🔴 THE BUG THIS REPLACES MADE THE WHOLE FEATURE UNREACHABLE, INTERMITTENTLY.
    // The query string is the only carrier between the picker and this screen that is certain to
    // survive: `Redirect` (helpers/Routes/Redirect.ts:63-67) is an in-app `navigate()` unless the
    // URL contains "aspx" or "/pulseem/" (or equals the current path), in which case it is a
    // full page load — and `setCampaignContext` wipes the WhatsApp slice on a new id either way.
    // All three seeds here were gated on `waMappingStatus === 'succeeded'` — and, before
    // 09-channel-units, `GET WhatsApp/Mapping` FAILED with `409 WA_EMAIL_LEG_MISSING` for as long
    // as the campaign had no `CampaignsToDataSources` row (`_GetMapping_Stage` returns `-16`),
    // which was the state of a campaign never mapped: that row was created by the first save on
    // this screen — after the point where the seed was stuck. (Since 09-channel-units the
    // WhatsApp unit's row exists from its creation, POST WhatsApp/Container.)
    // ⇒ `withWhatsApp` stayed false ⇒ `selectWaActive` false ⇒ `renderEmailBlocks()` ⇒ no tabs,
    // no template card, no "אשר תבנית". It only worked once a source happened to be linked —
    // which is why it read as intermittent.
    //
    // 🔴 AND IT IS THE SAME MISTAKE AS C-29, ONE EFFECT OVER: we demanded the server's answer
    // before honouring an intent the operator had already expressed. The URL is a statement of
    // intent; it does not depend on anything.
    //
    // ⚠️ IT MUST STILL RUN AFTER THE RESET. `setCampaignContext` (dispatched in a LATER effect,
    // at the mount) wipes this slice, so a seed that ran before it would be erased. The gate is
    // therefore the reset's own observable result — the slice's `campaignId` matching this
    // screen's — and NOT effect declaration order, which is invisible at the call site and
    // silently reversible by anyone who moves a block.
    //
    // ⚠️ "server > URL > slice" IS PRESERVED, BY ORDERING RATHER THAN BY A GUARD: this runs
    // long before any answer lands, and `getWaMapping.fulfilled` overwrites `whatsAppTemplateId`
    // from the unit's own answer (`slice`, the `d.HasLeg` branch — a unit's GetMapping on
    // channel 3 reports HasLeg = 1). What it must NOT do — and no longer can — is let an answer
    // turn OFF a channel the URL switched on: §6's order of truth is about WHICH TEMPLATE, not
    // about whether the channel exists.
    //
    // ⚠️ AND NOTHING LIGHTS WITHOUT THE URL SAYING SO (invariant 8): with no `?tab=whatsapp`
    // this dispatches nothing at all, and the e-mail-only screen is untouched.
    const urlTemplate = searchParams.get('waTemplate');
    const urlFrom = searchParams.get('waFrom');
    // 09-channel-units — `?waOnly=1` is gone: "WhatsApp only" is the page BEING the WhatsApp unit
    // (`?channel=3`), not a bit on an e-mail campaign, so there is nothing left for it to seed.
    const urlWantsWa = searchParams.get('tab') === 'whatsapp';
    // The reset's observable result — see above.
    const waSliceCampaignId = useSelector((st: any) => st.smartSendWa.campaignId);
    // Latched PER CAMPAIGN, not per mount: React Router reuses this component across an `:id`
    // change, so a boolean latch would swallow the parameters on the second campaign reached in
    // one session. The latch is also what stops the `?waOnly` seed from re-raising the unsaved
    // marker every time the autosave lowers it — a save loop, once per round trip, forever.
    const urlSeeded = useRef<number | null>(null);
    useEffect(() => {
        if (!waAllowed || waSliceCampaignId !== campaignId) return;
        // 09-channel-units — only on a page that carries a WhatsApp unit; an e-mail-only page takes
        // no WhatsApp seed whatever its query string says.
        // 12-fix-wave 7.4(a) — tested BEFORE the latch: on an e-mail page the unit is known only once
        // the row has been read, so latching first would drop the seed of every "both" page.
        if (waUnitId == null) return;
        if (urlSeeded.current === campaignId) return;
        urlSeeded.current = campaignId;
        // ONE effect for all three, so their order is a fact rather than a coincidence of
        // declaration: the channel must exist before a template or a channel choice can mean
        // anything on it.
        if (urlWantsWa) dispatch(setWithWhatsApp(true));
        if (urlTemplate || urlFrom) dispatch(seedWaFromUrl({ templateId: urlTemplate, fromNumber: urlFrom }));
    }, [dispatch, waAllowed, waSliceCampaignId, campaignId, waUnitId, urlWantsWa, urlTemplate, urlFrom]);

    // ── auto-save (replaces the manual Save button) ─────────────────────────────────────────────
    // saveState drives the header status indicator. The refs coordinate a DEBOUNCED, SINGLE-FLIGHT
    // persist: only one setMapping is ever in flight, and edits made while one is running are
    // coalesced into a single trailing re-save — so two saves can never land out of order and
    // silently drop a column (each save is a full snapshot and setMapping has no server ordering
    // guard). sendLockedRef latches auto-save OFF once the campaign is sending (-6), so a permanent
    // 409 can never turn into a retry loop.
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error' | 'locked'>('idle');
    // QA BUG-25 — what the indicators SHOW (ActionBar and the e-mail save bar): never «כל השינויים נשמרו»
    // while an edit is still unsaved — inside the 750 ms debounce, or held behind the exit dialog (QA BUG-10).
    // `saveState` itself, and every save path that sets it, are unchanged.
    // QA BUG-25-r1 — the e-mail save bar stays MOUNTED in that state (no flicker, no content jump) and says
    // «יש לך שינויים לא שמורים במיפוי השדות.» (`saveBar.unsaved`) on a neutral background instead.
    const savedButDirty = saveState === 'saved' && dirty;
    const shownSaveState = savedButDirty ? 'idle' : saveState;
    const saveTimer = useRef<any>(null);
    const saveInFlight = useRef(false);
    const savePending = useRef(false);
    const sendLockedRef = useRef(false);
    // MONOTONIC EDIT COUNTER. Bumped by markEdited() on every user edit, captured when a save
    // request is BUILT, and re-read when it resolves. It exists because `dirty` alone cannot
    // distinguish "the edits I just saved" from "edits that arrived while I was saving", and
    // clearing it unconditionally on a 200 STRANDS the latter:
    //   t=0    edit          → dirty=true, timer armed for t=750
    //   t=750  timer fires   → buildSaveRequest() snapshots state HERE; request in flight
    //   t=900  edit          → dirty is ALREADY true so nothing changes, but the debounce effect
    //                          re-runs on the new tokenMap and arms a fresh timer for t=1650
    //   t=1200 200 comes back→ setDirty(false) ⇒ the effect's cleanup CLEARS the t=1650 timer and
    //                          its body then bails on !dirty. The t=900 edit is never sent, and
    //                          the sticky bar reads "כל השינויים נשמרו".
    // `savePending` does NOT cover this: it is only set when runAutoSave is CALLED while a save is
    // in flight (see below), which needs the round trip to outlast the 750ms debounce. The window
    // above — the common one, where the response beats the next timer — stays wide open.
    // The falsely-cleared `dirty` is not only a lost edit: it also disarms the beforeunload handler,
    // the three-way exit confirm, and TestSendDialog's "the preview uses the SAVED mapping" warning,
    // so the operator's one pre-send check silently answers wrong.
    const editSeq = useRef(0);

    const showToast = (ok: boolean, msg: string) => setToast({ open: true, ok, msg });

    // The ONE place an edit is recorded. Both onChange handlers below route through it so the
    // counter and `dirty` can never disagree — two call sites setting them separately WILL drift.
    // 🔴 AND the e-mail tab's write lock lives here too (invariant 24(2)). The comment above is
    // exactly why: this is the ONE place an edit is recorded, so a no-op here disarms `dirty`,
    // the exit dialog and `beforeunload` together. Without it a tab declared "locked" would
    // still open the unsaved-changes prompt and still warn on refresh.
    const markEdited = () => { if (emailLocked) return; editSeq.current += 1; setDirty(true); };

    useEffect(() => {
        // `accountFeatures?.length &&`, NOT `accountFeatures &&` — the same guard SmartSendPicker.tsx:38,
        // DataSources.tsx:90 and DataSourceView.tsx:73 use. The cookie-backed list is null until it
        // loads (commonSlice.js:239) and a bare truthiness check would let an unentitled user through
        // on `undefined`; `?.length` also covers an empty array. Redirect only once features arrived.
        if (accountFeatures?.length && accountFeatures.indexOf(PulseemFeatures.DATA_SOURCES) === -1) {
            // `sitePrefix` is `string | undefined` (it comes straight from process.env), so it
            // needs the same `?? ''` the other feature-gate redirects use — see
            // DataSources.tsx:88 and DataSourceView.tsx:72.
            Redirect({ url: sitePrefix ?? '', openNewTab: false });
        }
    }, [accountFeatures]);

    // The "send anyway" confirmation must apply ONLY to the exact unmapped set the user
    // acknowledged — reset it whenever the mapping/columns change (e.g. a source switch
    // clears the map), so a stale confirm can never bypass the §16 raw-token gate.
    useEffect(() => {
        setConfirmUnmapped(false);
    }, [smartSend.columns, smartSend.tokenMap]);

    // Nothing is unsaved right after the mapping (re)loads — getMapping.fulfilled reseeds
    // `tokens` AND `tokenMap` from the server — nor right after a source switch, which empties
    // the map (selectSource, smartSendSlice.ts:292). `tokens` is a fresh array on every load, so
    // its identity is the load signal; a plain setTokenMapping never touches it.
    useEffect(() => {
        setDirty(false);
        // Same signal, same meaning: `tokens` is a fresh array only on a SUCCESSFUL getMapping
        // (the failure branches write mappingStatus/mappingError and nothing else), so this clears
        // the restore error exactly when a reload has landed — and on any source switch.
        setRestoreFailed(false);
    }, [smartSend.tokens, currentSourceId]);

    // item 3: warn on tab close / refresh while there are unsaved mapping changes. react-router
    // 6.4.2 exposes no useBlocker, so this covers only real unloads; in-SPA navigation is
    // mitigated by the sticky save bar. Pattern mirrors LegacyPageFrame.tsx:17-27.
    useEffect(() => {
        // 🔴 `waDirty || suppressEmailDirty` too. The autosave debounce is 750 ms; inside it the
        // last binding exists only on screen. On a WhatsApp-only send this gate was watching
        // `dirty`, which is the E-MAIL mapping's flag and is false for the whole session — so the
        // one channel that had unsaved work was the one channel that got no warning.
        if (!dirty && !waDirty && !suppressEmailDirty) return undefined;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty, waDirty, suppressEmailDirty]);

    useEffect(() => {
        if (!campaignId || Number.isNaN(campaignId) || campaignId <= 0) return;
        dispatch(setCampaignContext({ campaignId, dataSourceId: parsedDsId }));
        // 🔴 THE URL, NOT `smartSend.selectedChannel`. The channel is written to the slice by a
        // DIFFERENT effect, so on the first pass this one still reads the previous value — 1 —
        // and fires GET Mapping for channel 1 against a WACampaignID. It never corrects itself
        // either, because `selectedChannel` was not in the dep array. The URL is the authority
        // here and it is already correct on mount.
        dispatch(getMapping({ campaignId, channel: Number(urlChannel) === 3 ? 3 : 1 }));
    }, [dispatch, campaignId, parsedDsId, urlChannel]);

    // 09-channel-units — the page's units, right AFTER the reset above (which wipes the WhatsApp
    // slice on a new id), so the reset can never erase them. Every WhatsApp selector reads them.
    // 12-fix-wave 7.4(a) — its own effect: on an e-mail page the unit arrives with the row, after the
    // mount, and must not re-run the mapping read above.
    useEffect(() => {
        if (!campaignId || Number.isNaN(campaignId) || campaignId <= 0) return;
        dispatch(setWaUnit({ unitId: waUnitId, unitMode }));
    }, [dispatch, campaignId, waUnitId, unitMode]);

    // 12-fix-wave 7.4(a) — A UNIT'S OWN PAGE WHOSE ROW HOLDS AN E-MAIL IS PART OF A "BOTH" SEND: go to
    // the "both" page, the e-mail's, on the row's source. `tab`, `waTemplate`, `waFrom` and `reused`
    // are kept.
    useEffect(() => {
        if (rowEmailOfWaPage == null) return;
        const qs = new URLSearchParams();
        if (mappingRow?.dataSourceId != null) qs.set('dataSourceId', String(mappingRow.dataSourceId));
        ['tab', 'waTemplate', 'waFrom', 'reused'].forEach((k) => {
            const v = searchParams.get(k);
            if (v != null) qs.set(k, v);
        });
        const q = qs.toString();
        Redirect({ url: `${sitePrefix}Campaigns/SmartSend/${rowEmailOfWaPage}${q ? `?${q}` : ''}`, openNewTab: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowEmailOfWaPage]);

    // 09-channel-units — THE PAGE'S UNITS END WITH THE PAGE. An in-app navigation keeps the store, and
    // no screen after this one may answer from this page's units (see `setWaUnit`, unitMode null).
    // 12-fix-wave 7.4(a) — and so does its row: the next visit reads the partner again.
    useEffect(() => () => { dispatch(setWaUnit({ unitId: null, unitMode: null })); dispatch(clearMappingRow()); }, [dispatch]);

    // ══ 08-mapping-memory — "USED LAST TIME" ON THE E-MAIL TOKENS ═══════════════════════════
    // For every ##token## this sub-account has mapped before, the source column chosen last time,
    // resolved by the server against the version on screen. It reaches the table as a
    // `suggestionSource` WITH the opt-in `fallbackToSimilarity`: memory goes FIRST, and a token with
    // no usable memory falls back to the name-similarity chip it had before this feature. Both are
    // the same one-click chip — nothing is written without the operator's click, and nothing here
    // or in the table offers to apply them in bulk (`suggestMapping.ts:16-44`).
    //
    // ONE GET per (campaign, version) — never per edit, never per autosave (an autosave moves
    // neither). A failed or absent answer leaves `[]`, and the table then renders exactly what it
    // rendered before. Asked only when:
    //   · the container is an E-MAIL campaign. 🔴 R1 IS CHANNEL 1 ONLY (Idan, 2026-09-23): the
    //     server refuses any other known channel with 400 DATA_INCORRECT, before ownership and
    //     before any read. Channel-3 memory has exactly one door, R2 (`getWaMemorySuggestions`,
    //     behind WaGate) — the WhatsApp tab's call, never this one. So a stand-alone WhatsApp
    //     container (`?channel=3`) never asks here, and every request names channel 1. The test is
    //     the mount effect's own reading of the URL (`=== 3 ? 3 : 1`, above), so this read and
    //     `getMapping` always describe the same container;
    //   · THIS campaign's mapping has loaded — the slice outlives a campaign-to-campaign navigation
    //     (`clearSmartSend` has zero dispatch sites), so until then `lockedVersionId` can still be
    //     the previous campaign's;
    //   · a version is on screen — `lockedVersionId` is the version of `smartSend.columns` (the same
    //     reducers write both) and the one `buildSaveRequest` posts. Idan E2: once the campaign is
    //     bound it is the server's locked version (`getMapping.fulfilled`); before that it is the
    //     picked source's ActiveVersionID (`loadSourceColumns.fulfilled`), so a brand-new campaign
    //     asks on entry, as soon as its source's columns are on screen;
    //   · there is a token — a campaign without ##tokens## has nothing to ask about. GetMapping
    //     extracts tokens on container channel 1 only, so today this gate ALSO keeps a channel-3
    //     container quiet — but the channel test above does not lean on it: it holds on its own,
    //     whatever the slice holds (tokens included), and after any edit that relaxes this gate.
    const memoryVersionId: number | null = smartSend.lockedVersionId ?? null;
    const memoryArg: MemorySuggestionsArg | null = (
        Number(urlChannel) !== 3
        && campaignId > 0 && !Number.isNaN(campaignId)
        && smartSend.campaignId === campaignId
        && smartSend.mappingStatus === 'succeeded'
        && memoryVersionId != null && memoryVersionId > 0
        && smartSend.tokens.length > 0
    ) ? {
        campaignId,
        // ALWAYS 1: R1 serves nothing else (see above). Still sent, because the route requires it
        // (an absent channel is refused, never defaulted). Neither `selectedChannel` nor the URL's
        // raw value — the gate above has already established that the container is e-mail.
        channel: eSendChannel.EMAIL,
        dataSourceVersionId: memoryVersionId,
    } : null;
    const memoryKey = memoryArg ? memorySuggestionsKey(memoryArg) : null;
    // Latched per KEY, like `urlSeeded` and `bindingWritten`: asked once per mount whatever it
    // answered, so a failing route (500 until the SQL is deployed) is never retried in a loop.
    // An answer that arrives after the key moved on is dropped in the slice (`meta.requestId`).
    const memoryRequested = useRef<string | null>(null);
    useEffect(() => {
        if (!memoryArg || !memoryKey || memoryRequested.current === memoryKey) return;
        memoryRequested.current = memoryKey;
        dispatch(getMappingSuggestions(memoryArg));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, memoryKey]);
    // Only rows that answer the key this screen would ask with NOW — see `memorySuggestionsKey`.
    const memoryRows = memoryKey != null && smartSend.memorySuggestionsKey === memoryKey
        ? smartSend.memorySuggestions
        : null;
    const memoryLiveIds = useMemo(
        () => new Set<number>(smartSend.columns.map((c: any) => c.ColumnID)),
        [smartSend.columns],
    );
    // E-mail rows are matched AS-IS (`VariableKey === tok.Token`) and offered only when the
    // remembered header is in this version AND on screen — `usableMemoryColumnId`, the same rule
    // the WhatsApp tab applies. `null` hands the row to the similarity fallback.
    const emailMemorySource = (tok: SmartSendTokenInfo): TokenSuggestion | null => {
        const row = memoryRowForToken(tok.Token, memoryRows);
        const columnId = usableMemoryColumnId(row, memoryLiveIds);
        if (row == null || columnId == null) return null;
        return {
            columnId,
            label: t('DataSources.send.mapping.usedLastTime', { name: memoryColumnName(row, smartSend.columns) }),
        };
    };

    // ── save flow (§13 step 6): setMapping (lock + PulseemDS_ group + TokenMap) → get
    // SyntheticGroupID → READ the campaign's current send settings → post them back with the
    // synthetic group MERGED into GroupIds (§10). Read-modify-write, never a partial body. ──
    const buildSaveRequest = () => {
        const colSet = new Set(smartSend.columns.map((c: any) => c.ColumnID));
        // EVERY ColumnID leaving this function must exist in the LOCKED version — not just the
        // token mappings. Added 2026-08-16. colSet was already built for Mappings (below) and the
        // filter there cites the SP's -9; the business columns are checked by a SECOND, separate
        // guard in the same proc — dbo.CampaignsToDataSources_Set.StoredProcedure.sql:133-145 —
        // which LEFT JOINs (@prm_SupervisorColumnID, @prm_GapColumnID, @prm_SortColumnID) against
        // DataSourceColumns for @LockVersionID and returns -9 if any non-null id is missing. These
        // three lines used to post state verbatim, so one stale id failed the ENTIRE save, and
        // failed it in the worst possible shape: the mapping edit the operator was making is
        // rejected because of a business column they never touched, the error surfaces as the
        // generic retry text, and Retry (the failed-save button at :844) rebuilds the
        // identical request from the same
        // state — an unbreakable loop with no control on screen that can clear the offending value
        // (the picker cannot show, let alone deselect, a column that is no longer in `columns`).
        //
        // Scrubbing to null is the same treatment Mappings already gives a vanished id, and it is
        // the honest one: a column that is not in the locked version is not a business column any
        // more, so persisting "no supervisor / no shortfall" states what is actually true. It is
        // also recoverable — the value is only dropped on a save that would otherwise have been
        // rejected outright, and the picker is free to set a live column afterwards.
        //
        // Empty `columns` cannot reach here: the whole body renders behind `hasColumns` (:681),
        // the autosave effect bails on it first (:370), and the source-binding effect below waits
        // for `smartSend.dataSource`, which `loadSourceColumns.fulfilled` sets in the same reducer as
        // `columns`. (That third gate was missing until 2026-09-27; the binding effect was the one
        // caller that could arrive here with nothing loaded.) If it ever did, Mappings would already
        // be posting [] — a full mapping wipe — so these three are not the exposure.
        const inLockedVersion = (id: number | null | undefined): number | null =>
            (id != null && colSet.has(id)) ? id : null;
        return {
            CampaignID: campaignId,
            Channel: smartSend.selectedChannel,
            DataSourceID: smartSend.dataSource?.DataSourceID,
            DataSourceVersionID: smartSend.lockedVersionId,
            // A MACHINE GUESS IS NOT A DECISION AND IS NOT PERSISTED. Changed 2026-08-11
            // (deep review R1-02). pickDefaultSupervisorColumn fills this in with no user action —
            // then by the column NAME alone; since 2026-10-11 only an EMAIL column, still a guess — and
            // this line used to post it verbatim, so opening the send summary (which saves
            // silently) or letting the 750 ms autosave fire after any token edit was enough to
            // write a SupervisorColumnID the operator never chose.
            // null, not omitted: the field is bound server-side either way, and omitting it would
            // make the SP clear a value the operator HAD deliberately saved. Sending null while the
            // value is only a suggestion is the state that is actually true.
            // smartSendSlice.setBusinessColumn clears the flag the moment EITHER business picker is
            // touched — including a deliberate "None", and including the shortfall picker, which is
            // reachable only while a supervisor value is displayed beside it (see the
            // second-confirmation-point note in smartSendSlice.setBusinessColumn) — so a real
            // choice still persists on the next save.
            //
            // THE TWO RULES COMPOSE, and they compose in one direction only: the guess rule and the
            // locked-version rule both resolve to null, so a guessed value is posted as null even
            // when its column is perfectly valid, and a confirmed value is posted as null once its
            // column has vanished. Order is therefore irrelevant, but the guess test stays OUTSIDE
            // inLockedVersion on purpose — a guess must not become persistable merely by pointing
            // at a live column, which is what folding it into the same predicate would allow.
            // Both write null EXPLICITLY rather than omitting the field, for the reason above: the
            // field is bound server-side either way and an omission makes the SP clear a value the
            // operator deliberately saved.
            SupervisorColumnID: smartSend.supervisorColumnIsGuess
                ? null
                : inLockedVersion(smartSend.supervisorColumnId),
            // Gap and Sort have no guess flag — nothing ever fills them in without a click
            // (businessColumnDefaults.ts returns them untouched), so the locked-version scrub is
            // the whole rule for them.
            GapColumnID: inLockedVersion(smartSend.gapColumnId),
            SortColumnID: inLockedVersion(smartSend.sortColumnId),
            // Only tokens mapped to a column that still EXISTS in the locked version. A
            // vanished mapping (id ∉ columns) is treated as unmapped (sent raw) — the same
            // definition selectUnmappedTokens/TokenMappingTable use — otherwise the SP would
            // reject the whole save with -9 and the confirm-then-send path would dead-end.
            Mappings: smartSend.tokens
                .filter((tk: any) => { const c = smartSend.tokenMap[tk.Token]; return c && c > 0 && colSet.has(c); })
                .map((tk: any, i: number) => ({ Token: tk.Token, DataSourceColumnID: smartSend.tokenMap[tk.Token], GroupNo: 0, GroupTitle: null, DisplayOrder: i + 1 })),
            // 12-fix-wave 7.4(d) — while the page's reset confirmation is armed (sessionStorage).
            ...(isConfirmResetArmed(pageChannel, campaignId) ? { ConfirmReset: true } : {}),
        };
    };

    // ══ 12-fix-wave 7.4(d) — DECISION 1: A SOURCE/VERSION CHANGE RESETS BOTH CHANNELS' MAPPINGS ══════
    // The confirmation comes at «החלף מקור» (`goChangeSource`); a save the server still answers with 409
    // SOURCE_RESET_CONFIRM_REQUIRED (a source switch inside the page, or the key was lost) opens the SAME
    // dialog once, with both autosaves held. A save answered 409 SEND_ALREADY_SENT stops both autosaves
    // for good and says "send closed" — no dialog.
    // QA BUG-29 — 'entry': the URL's source differs from the send's row (see the entry check below).
    const [resetDlg, setResetDlg] = useState<null | 'changeSource' | 'retry' | 'entry'>(null);
    const [entryInfo, setEntryInfo] = useState<null | { target: number; from: string; to: string }>(null);
    // The mode the dialog SHOWS: the last one opened, so its text does not flip while it fades out.
    const shownResetModeRef = useRef<typeof resetDlg>(null);
    if (resetDlg != null) shownResetModeRef.current = resetDlg;
    // The request that opened the dialog (`first`), plus the last e-mail request refused while it was
    // up: the e-mail table's work is resent too. A WhatsApp save refused after the first is not
    // replayed (7.4(d)).
    const resetRetry = useRef<null | { first: 'email' | 'wa'; email: any | null }>(null);
    const resetBusy = useRef(false);
    const resetHoldRef = useRef(false);
    const closedHoldRef = useRef(false);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
    const setHold = (v: boolean) => { resetHoldRef.current = v; setResetHold(v); };

    const stopAutosaveTimers = () => {
        if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
        savePending.current = false;
        waAutosave.cancelPending();
    };

    const onResetRequired = (retry: { kind: 'email'; req: any } | { kind: 'wa' }) => {
        stopAutosaveTimers();
        // The answer to the dialog is being carried out (resend + reload); a 409 of a save made before
        // it is superseded by that reload and must not leave a request behind for a later dialog.
        if (resetBusy.current) return;
        if (!resetRetry.current) {
            resetRetry.current = { first: retry.kind, email: retry.kind === 'email' ? retry.req : null };
        } else if (retry.kind === 'email') {
            resetRetry.current.email = retry.req;
        }
        if (resetHoldRef.current) return;       // the dialog is already up: opened once
        setHold(true);
        setExitOpen(false);
        setResetDlg('retry');
    };

    // A refused e-mail save: its `errors.*` text (ERR_KEY), else — for a WhatsApp code the save of a unit's
    // row can answer (WA_ALREADY_SENT / WA_EDIT_BLOCKED_DURING_SEND, contract 5.7) — its existing
    // `whatsapp.error.*` text, else the generic one.
    const saveErrorText = (code: string | null | undefined): string => {
        if (code && ERR_KEY[code]) return t('DataSources.send.errors.' + ERR_KEY[code]);
        const waKey = `DataSources.send.whatsapp.error.${code}`;
        if (code && code.indexOf('WA_') === 0 && i18n.exists(waKey)) return t(waKey);
        return t('DataSources.send.errors.generalError');
    };

    const onSendClosed = () => {
        stopAutosaveTimers();
        if (closedHoldRef.current) return;      // said once
        closedHoldRef.current = true;
        setClosedHold(true);
        // Whichever save was refused (e-mail or WhatsApp, auto or explicit), the e-mail autosave latches
        // off and the save bar states it ("send closed", see the 'locked' arm of the bar).
        sendLockedRef.current = true;
        setSaveState('locked');
        showToast(false, t('DataSources.send.errors.sendClosed'));
    };

    // "Reload both mappings": the e-mail page's own read and the unit's.
    // `restoring` keeps the body (tabs, action bar) mounted through the reload — the restore path's guard.
    const reloadBothMappings = async () => {
        setRestoring(true);
        const jobs: Promise<any>[] = [dispatch(getMapping({ campaignId, channel: pageChannel })) as any];
        if (waUnitId != null) jobs.push(dispatch(getWaMapping({ campaignId: waUnitId, channel: 3 })) as any);
        await Promise.all(jobs);
        setRestoring(false);
    };

    // The dialog's «אפס והמשך».
    //   · at «החלף מקור»: arm the confirmation for this page, then leave to the picker as before;
    //   · after a 409: resend THAT request with ConfirmReset: true; on 200 reload both mappings. A
    //     pending WhatsApp save is not replayed (its timer was cancelled; the reload replaces the map).
    const confirmResetDialog = async () => {
        const mode = resetDlg;
        if (mode == null || resetBusy.current) return;      // one answer per dialog
        setResetDlg(null);
        if (mode === 'changeSource') { leaveToPicker(true); return; }
        // QA BUG-29 — «המשך» of the entry dialog: switch to the URL's source now (one save + reload).
        if (mode === 'entry') {
            const info = entryInfo;
            if (info) await applyEntryReset(info.target);
            else setHold(false);
            return;
        }
        resetBusy.current = true;
        try {
            const retry = resetRetry.current;
            resetRetry.current = null;
            let ok = !!retry;
            if (retry && retry.first === 'wa') {
                const r = await waAutosave.saveWaMapping({ confirmReset: true });
                ok = r.ok;
                // SEND_ALREADY_SENT reaches `onSendClosed` through the WhatsApp save-error effect below;
                // this code never toasts (it is the dialog's own question).
                if (!ok && r.message !== 'SEND_ALREADY_SENT' && r.message !== 'SOURCE_RESET_CONFIRM_REQUIRED') {
                    showToast(false, waErrorText(r.message));
                }
            }
            if (ok && retry && retry.email) {
                const res: any = await dispatch(setMapping({ ...retry.email, ConfirmReset: true }));
                const r = res && res.payload ? res.payload : {};
                ok = r.StatusCode === 200;
                if (!ok) {
                    if (r.Message === 'SEND_ALREADY_SENT') onSendClosed();
                    else if (r.Message !== 'SOURCE_RESET_CONFIRM_REQUIRED') showToast(false, saveErrorText(r.Message));
                }
            }
            if (ok) {
                clearConfirmReset(pageChannel, campaignId);
                await reloadBothMappings();
            }
        } finally {
            resetRetry.current = null;
            setHold(false);
            resetBusy.current = false;
        }
    };

    // The dialog's «ביטול».
    //   · at «החלף מקור»: stay;
    //   · after a 409: the page goes back to the row's source (the URL's `dataSourceId` := the row's
    //     DataSourceID from the last read), both mappings are read again, autosave resumes.
    const cancelResetDialog = async () => {
        const mode = resetDlg;
        if (resetBusy.current) return;
        setResetDlg(null);
        // QA BUG-29 — «ביטול» of the entry dialog: nothing is written; the page goes to the row's source
        // (the URL's `dataSourceId` := the row's) and any confirmation left for this page is dropped.
        if (mode === 'entry') {
            clearConfirmReset(pageChannel, campaignId);
            backToRowSource();
            setHold(false);
            return;
        }
        if (mode !== 'retry') return;
        resetBusy.current = true;
        resetRetry.current = null;
        try {
            const rowDs = mappingRow?.dataSourceId ?? null;
            if (rowDs != null && searchParams.get('dataSourceId') !== String(rowDs)) {
                const sp = new URLSearchParams(searchParams);
                sp.set('dataSourceId', String(rowDs));
                setSearchParams(sp, { replace: true });
            }
            await reloadBothMappings();
        } finally {
            resetRetry.current = null;
            setHold(false);
            resetBusy.current = false;
        }
    };

    // ══ QA BUG-29 — THE URL'S SOURCE IS CHECKED AGAINST THE SEND'S ROW ON ENTRY ═══════════════════════
    // `getMapping` loads the row's source (dataSource, columns, token map) and the URL's `dataSourceId`
    // was never compared with it: entering from source X into a send mapped to Z silently showed Z (and
    // K-01: the "החלף מקור" round trip came back to Z). Idan R1: the source the operator came with is
    // FIXED; switching to it resets the send's previous mapping, after a confirmation. So, on an e-mail or
    // "both" page (never on a unit's own page, `?channel=3`, whose URL the row redirect owns), once THIS
    // page's row is read and it names a source other than the URL's:
    //   · the send is closed (D1), or the URL's source is not a READY source of this account ⇒ no switch:
    //     the URL goes back to the row's source and the page says why;
    //   · the switch was already confirmed for this page — on the picker (the key holds this target) or
    //     at «החלף מקור» (the '1' key) ⇒ switch now, without asking again;
    //   · otherwise ⇒ the entry dialog: «המשך» switches, «ביטול» goes back to the row's source.
    // THE SWITCH IS ONE EXPLICIT SAVE of the URL's source with an empty map and ConfirmReset (version
    // null = the source's active version, Set :121-122), then both mappings are read again — the proven
    // path of the 409 dialog. Not a client-side selectSource with a lazy first save: that left the
    // WhatsApp tab on the old map and the restore banner offering Z back while the URL says X.
    // When the row agrees with the URL (or names no source), a confirmation left for this page is stale
    // and is dropped. Latched per (campaign, URL source, row source), so a re-read never asks twice.
    const dsList = useSelector((s: any) => s.dataSources.list);
    const dsListStatus = useSelector((s: any) => s.dataSources.listStatus);
    const waMappingStatus = useSelector((s: any) => s.smartSendWa.mappingStatus);
    const entryChecked = useRef<string | null>(null);

    // QA BUG-32 — DID THE PICKER OPEN THIS PAGE JUST NOW? Its one-shot mark (`markConfirmEntry`), taken on
    // mount. Only then is a reset confirmation armed for this page trusted (the picker's R1 «המשך», or the
    // change-source trip); a later visit — a direct link, Back, an old tab — finds no mark, drops the key
    // and asks. A unit's own page never takes part in the entry check.
    const entryFromPickerRef = useRef(false);
    useEffect(() => {
        entryFromPickerRef.current = !isWaPage && takeConfirmEntry() === campaignId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // QA BUG-32 — set by `doExit` before it arms the change-source confirmation and leaves. From then on the
    // entry check stays out: React 17 does not batch the post-await «שמור וצא» path, so the check could run
    // between that arm and the unmount and drop the '1' key it had just armed (a second R1 dialog).
    const exitingRef = useRef(false);
    // QA BUG-32 — the header SourcePicker's list request has ENDED since this page mounted ('loading' seen,
    // then 'succeeded'/'failed'), whatever it left in the store: an error envelope answers 'succeeded' and
    // keeps the old list, which must not make the entry check wait forever.
    const listLoadingSeenRef = useRef(false);
    const listSettledRef = useRef(false);
    useEffect(() => {
        if (dsListStatus === 'loading') listLoadingSeenRef.current = true;
        else if (listLoadingSeenRef.current && (dsListStatus === 'succeeded' || dsListStatus === 'failed')) listSettledRef.current = true;
    }, [dsListStatus]);

    // The URL's `dataSourceId` := the row's source (as `cancelResetDialog` does). The mount effect re-runs
    // on the new value: it re-keys the slice and reads the row again.
    const backToRowSource = () => {
        const rowDs = mappingRow?.dataSourceId ?? null;
        if (rowDs != null && searchParams.get('dataSourceId') !== String(rowDs)) {
            const sp = new URLSearchParams(searchParams);
            sp.set('dataSourceId', String(rowDs));
            setSearchParams(sp, { replace: true });
        }
    };

    const applyEntryReset = async (target: number) => {
        if (resetBusy.current) return;
        resetBusy.current = true;
        setHold(true);
        stopAutosaveTimers();
        try {
            const req: SaveMappingRequest = {
                CampaignID: campaignId,
                Channel: pageChannel,
                DataSourceID: target,
                DataSourceVersionID: null,
                SupervisorColumnID: null,
                GapColumnID: null,
                SortColumnID: null,
                Mappings: [],
                ConfirmReset: true,
            };
            const res: any = await dispatch(setMapping(req));
            const r = res && res.payload ? res.payload : {};
            // Used either way: a refused switch must not linger and switch a later visit unasked.
            clearConfirmReset(pageChannel, campaignId);
            // QA BUG-32 — the page was left while the save was on the wire: no reload (it would re-seed the
            // next screen's slice) and no URL rewrite (it would pull the operator back here).
            if (!mountedRef.current) return;
            if (r.StatusCode === 200) {
                await reloadBothMappings();
            } else {
                if (r.Message === 'SEND_ALREADY_SENT') onSendClosed();
                else showToast(false, saveErrorText(r.Message));
                backToRowSource();
            }
        } finally {
            if (mountedRef.current) setHold(false);
            resetBusy.current = false;
        }
    };

    useEffect(() => {
        if (pageChannel !== 1 || parsedDsId == null) return;
        if (exitingRef.current) return;   // QA BUG-32 — leaving: see `exitingRef`
        if (smartSend.mappingStatus !== 'succeeded' || mappingRow == null) return;
        if (resetDlg != null || resetBusy.current || exitOpen) return;
        const rowDs = mappingRow.dataSourceId;
        if (rowDs == null || rowDs === parsedDsId) {
            if (confirmResetTarget(pageChannel, campaignId) != null || isConfirmResetArmed(pageChannel, campaignId)) {
                clearConfirmReset(pageChannel, campaignId);
            }
            return;
        }
        const latch = `${campaignId}-${parsedDsId}-${rowDs}`;
        if (entryChecked.current === latch) return;
        // A "both" page decides "closed" from its WhatsApp unit's statuses: wait for that read, unless it
        // cannot come (WhatsApp not allowed, or its Availability failed).
        const waKnown = waUnitId == null || !waAllowed || waAvailabilityStatus === 'failed'
            || waMappingStatus === 'succeeded' || waMappingStatus === 'failed';
        if (!waKnown) return;
        // The source list (the header's SourcePicker fetches PageSize 100): only that list can say the
        // URL's source is not there. A smaller page left by the DataSources screen is not evidence.
        const fullList = !!dsList && (dsList.pageSize ?? 0) >= 100;
        // QA BUG-32 — or THIS page's request has ended without one (then the server is the net).
        if (!fullList && !listSettledRef.current && dsListStatus !== 'failed') return;
        entryChecked.current = latch;
        const targetRow: any = fullList ? (dsList.items || []).find((it: any) => it.DataSourceID === parsedDsId) : null;
        if (sendIsClosed || (fullList && (!targetRow || targetRow.Status !== eDataSourceStatus.READY))) {
            clearConfirmReset(pageChannel, campaignId);
            showToast(false, sendIsClosed
                ? t('DataSources.send.errors.sendClosed')
                : t('DataSources.send.source.entryTargetUnavailable'));
            backToRowSource();
            return;
        }
        // QA BUG-32 — a confirmation counts only on the entry the picker just made (`entryFromPickerRef`);
        // a key met on any other entry is left over from an earlier visit: dropped, and the dialog asks.
        const armed = confirmResetTarget(pageChannel, campaignId) === parsedDsId || isConfirmResetArmed(pageChannel, campaignId);
        if (armed && entryFromPickerRef.current) {
            applyEntryReset(parsedDsId);
            return;
        }
        clearConfirmReset(pageChannel, campaignId);
        stopAutosaveTimers();
        setHold(true);
        setEntryInfo({
            target: parsedDsId,
            from: saved?.dataSourceName ?? smartSend.dataSource?.Name ?? String(rowDs),
            to: targetRow?.Name ?? String(parsedDsId),
        });
        setResetDlg('entry');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageChannel, parsedDsId, campaignId, smartSend.mappingStatus, mappingRow, resetDlg, exitOpen,
        waUnitId, waAllowed, waAvailabilityStatus, waMappingStatus, dsList, dsListStatus, sendIsClosed]);

    // QA BUG-32 — THE ROW AND THE URL DISAGREE AND THE ENTRY CHECK HAS NOT DECIDED YET (it waits for the
    // WhatsApp unit's statuses and the source list, about a round trip). Until it decides, the body and the
    // ActionBar stay out: an edit or a "שלח לכולם" in that window acted on the row's OLD source — an edit
    // could use up a confirmation, a «ביטול» then dropped it, and a send could race the reset.
    const entryRowDs: number | null = pageChannel === 1 && parsedDsId != null && smartSend.mappingStatus === 'succeeded'
        ? (mappingRow?.dataSourceId ?? null) : null;
    const entryPending = entryRowDs != null && entryRowDs !== parsedDsId
        && entryChecked.current !== `${campaignId}-${parsedDsId}-${entryRowDs}`;

    // 12-fix-wave 7.4(d) — the WhatsApp saves (autosave or explicit) report through the slice. Both
    // codes are answered by the page: the dialog, or "send closed". The code is taken off the slice
    // at once so the next 409 is seen again.
    const waLastSaveError: string | null = useSelector((s: any) => s.smartSendWa.lastSaveError ?? null);
    useEffect(() => {
        if (waLastSaveError === 'SOURCE_RESET_CONFIRM_REQUIRED') {
            dispatch(setWaSaveState({ state: 'idle' }));
            onResetRequired({ kind: 'wa' });
        } else if (waLastSaveError === 'SEND_ALREADY_SENT') {
            onSendClosed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waLastSaveError]);

    // 12-fix-wave 7.4(a) — 409 SEND_USES_UNIT: the send's WhatsApp is a unit that this page did not know
    // of. The page reads its mappings again instead of showing an error.
    const waUsesUnit: boolean = useSelector((s: any) => {
        const w = s.smartSendWa;
        return [w.mappingError, w.lastSaveError, w.summaryError, w.previewError, w.waTestError].indexOf('SEND_USES_UNIT') > -1;
    });
    useEffect(() => {
        if (!waUsesUnit) return;
        dispatch(clearWaUsesUnitErrors());
        dispatch(readMappingRow({ campaignId, channel: pageChannel }));
        if (waUnitId != null) dispatch(getWaMapping({ campaignId: waUnitId, channel: 3 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waUsesUnit]);

    // ── save flow, SPLIT (item 2): saveMapping persists the mapping and returns the synthetic
    //    group id but does NOT attach it; attachGroup performs the GroupIds merge (the mutation
    //    that decides who the campaign sends to). Opening the summary — or saving from the test
    //    dialog — calls saveMapping only; the attach is deferred to a confirmed Send. ──
    const saveMapping = async (silent?: boolean): Promise<{ ok: boolean; gid: number | null; code?: string | null }> => {
        // 🔴 INVARIANT 24(1). The e-mail of this campaign has already gone out, so nothing here
        // may write: `CampaignsToDataSources_Set` blocks only `Campaigns.Status = 2`, so a save
        // on `Status = 4` SUCCEEDS and delete-and-reinserts the token map — and the supervisor
        // mail (§3.10) and "view message" (§3.7) resolve that map LIVE, so they would change
        // under a campaign that was already delivered.
        //
        // Returning here covers all THREE callers at once — `openSummary` (which saves with no
        // `dirty` check), `handleSaveAndExit`, and `TestSendDialog` via `onSaveMapping` — and the
        // third is the reason the guard cannot live in the callers: `TestSendDialog.tsx` is
        // byte-identical by contract and must not be edited.
        //
        // `{ ok: true, gid: null }` and not `{ ok: true }`: the live signature is
        // `Promise<{ok: boolean; gid: number|null}>` and the short form does not type-check.
        if (emailLocked) return { ok: true, gid: null };
        setSaving(true);
        // Snapshot the edit counter alongside the request body, for the same reason runAutoSave
        // does. This path is NOT modal-protected: openSummary calls saveMapping(true) and then
        // awaits fillAndSummarize + getSendSummary + getEmailSendSettings — four sequential round
        // trips — before setSummaryOpen(true), and the mapping table is fully interactive for all
        // of them. An edit made in that window must not be marked saved.
        const seq = editSeq.current;
        const req = buildSaveRequest();
        const res: any = await dispatch(setMapping(req));
        const r = res && res.payload ? res.payload : {};
        if (r.StatusCode !== 200) {
            setSaving(false);
            // 12-fix-wave 7.4(d) — never a toast for this code: it opens the reset dialog.
            if (r.Message === 'SOURCE_RESET_CONFIRM_REQUIRED') { onResetRequired({ kind: 'email', req }); return { ok: false, gid: null, code: r.Message }; }
            if (r.Message === 'SEND_ALREADY_SENT') { onSendClosed(); return { ok: false, gid: null, code: r.Message }; }
            showToast(false, saveErrorText(r.Message));
            return { ok: false, gid: null, code: r.Message ?? null };
        }
        // 12-fix-wave 7.4(d) — the first save that CARRIED the confirmation and answered 200 ends it.
        if ((req as any).ConfirmReset) clearConfirmReset(pageChannel, campaignId);
        const gid = r.Data && r.Data.SyntheticGroupID;
        setSaving(false);
        // The mapping is persisted — the unsaved-changes bar must go away, but ONLY if what landed
        // is still what is on screen. If the operator edited during the round trip the screen is
        // genuinely dirty and must stay so: the debounce effect then keeps its timer and persists
        // the newer state. `ok: true` is still correct — the request itself succeeded, which is
        // what the send/exit callers are asking about.
        if (editSeq.current === seq) setDirty(false);
        if (!silent) showToast(true, t('DataSources.send.toasts.mappingSaved'));
        return { ok: true, gid: gid != null ? Number(gid) : null };
    };

    // 09-channel-units — THERE IS NO CLIENT-SIDE "SOURCE BINDING" EFFECT, AND THERE MUST NOT BE ONE.
    // One stood here from 2026-09-23: it posted an e-mail `setMapping` on entry so that a
    // CampaignsToDataSources row existed before the first WhatsApp save, because the WhatsApp half of
    // "both" read the E-MAIL campaign's row. Every channel is now its own unit with its own row:
    //   · the WhatsApp unit is created WITH its row by the picker (`POST WhatsApp/Container` carries
    //     the source), and its own save re-binds that row when the source on screen changes;
    //   · the e-mail unit's row is written by the e-mail's own save, as it always was.
    // Nothing waits on another channel's write, so nothing has to be written ahead of the operator.

    // Auto-save the mapping. Calls setMapping ONLY (never attachGroup / fillAndSummarize), so it can
    // NEVER arm a send — the persist/arm split is preserved. Single-flight with a trailing coalesce.
    const runAutoSave = async () => {
        // 12-fix-wave 7.4(d) — a trailing re-fire that outlived the page never goes out: built after the
        // exit armed the reset confirmation, it would carry it and use it up before the picker returns.
        if (!mountedRef.current) return;
        if (sendLockedRef.current) return;
        // 12-fix-wave 7.4(d) — no autosave while the reset dialog is up or once the send is closed.
        if (resetHoldRef.current || closedHoldRef.current) return;
        // QA BUG-10 — nor behind the exit dialog; `dirty` stays, so cancel re-arms and "save" saves.
        if (exitOpenRef.current) return;
        if (saveInFlight.current) { savePending.current = true; return; }
        saveInFlight.current = true;
        setSaveState('saving');
        // Captured on the same line-of-execution as the request body, so `seq` describes EXACTLY
        // the state this request carries. Everything below compares against it.
        const seq = editSeq.current;
        const req = buildSaveRequest();
        const res: any = await dispatch(setMapping(req));
        const r = res && res.payload ? res.payload : {};
        saveInFlight.current = false;
        // TRUE when the operator edited while this request was on the wire. The response is then a
        // truthful answer about a snapshot that is no longer on screen, and treating it as "saved"
        // is what strands the edit (see the editSeq header).
        const superseded = editSeq.current !== seq;
        // 12-fix-wave 7.4(d) — only a save that CARRIED the confirmation uses it up.
        if (r.StatusCode === 200 && (req as any).ConfirmReset) clearConfirmReset(pageChannel, campaignId);
        // The send closed while this save was on the wire: autosave is off and the bar says so; this
        // answer changes nothing on screen.
        if (closedHoldRef.current) return;
        if (r.Message === 'SOURCE_RESET_CONFIRM_REQUIRED') {
            // 12-fix-wave 7.4(d) — the dialog, not an error bar; `dirty` stays for the resend.
            setSaveState('idle');
            onResetRequired({ kind: 'email', req });
            return;
        }
        if (r.Message === 'SEND_ALREADY_SENT') {
            // 12-fix-wave 7.4(d) — the send is closed: autosave stops for good (the same latch as -6).
            sendLockedRef.current = true;
            setSaveState('locked');
            onSendClosed();
            return;
        }
        if (r.StatusCode === 200) {
            if (superseded) {
                // Do NOT clear dirty and do NOT paint "נשמר" — the screen really does hold unsaved
                // work. Stay on 'saving'; the re-fire below sends the newer snapshot.
                setSaveState('saving');
            } else {
                setDirty(false);
                setSaveState('saved');
            }
        } else if (r.Message === 'EDIT_BLOCKED_DURING_SEND') {
            sendLockedRef.current = true;          // campaign is sending — stop auto-saving
            setSaveState('locked');
        } else {
            setSaveState('error');                 // keep dirty=true so Retry + beforeunload stay armed
            // Remember WHOSE save failed. Retry rebuilds from fresh state, so once the operator has
            // switched source it would post the new source's snapshot under the guise of retrying
            // the old one. The bar keeps reporting the failure either way — swallowing it is not an
            // option, because the same source switch also clears `dirty` and disarms beforeunload,
            // so the red bar is the ONLY remaining evidence that N mappings were never persisted.
            setFailedSaveSourceId(smartSend.dataSource?.DataSourceID ?? null);
        }
        // Re-fire once with the latest snapshot if edits arrived while this save was in flight.
        // `superseded` is the case the old `savePending` flag could not see: savePending is only
        // set when runAutoSave is CALLED mid-flight, which needs the round trip to outlast the
        // 750ms debounce, whereas superseded catches every edit regardless of timing. Both re-fire
        // through the same line so a save can still never overlap another (saveInFlight guards the
        // entry). The recursion terminates because editSeq only advances on human input.
        if ((savePending.current || superseded) && !sendLockedRef.current) {
            savePending.current = false;
            runAutoSave();
        }
    };

    // Debounced trigger: whenever the mapping changes and there is something to persist, schedule a
    // single trailing save (~750ms). CREATE-ON-FIRST-PICK — an empty post-source-select map has
    // nothing to save, so merely picking a source writes nothing to the (shared prod) DB. Driven off
    // the slice fields, NOT the onChange handlers, so buildSaveRequest reads FRESH post-dispatch state.
    useEffect(() => {
        if (!hasColumns || !dirty || sendLockedRef.current) return undefined;
        // 12-fix-wave 7.4(d) — no new timer while autosave is held. QA BUG-10 — the exit dialog holds it too.
        if (resetHold || closedHold || exitOpen) return undefined;
        // Bit-identical to the inline expression this replaced — selectHasMappingWork IS those four
        // clauses, including the `&& !supervisorColumnIsGuess` added 2026-08-11 (deep review R1-02):
        // an unconfirmed guess must not be the reason a save fires, or the helper's own output
        // triggers the autosave that then persists it and the value becomes its own justification.
        // Extracted so SourcePicker's confirm gate reads the SAME rule; two private copies drift,
        // and the two consumers disagreeing is how a confirm starts appearing over nothing.
        const hasAnyMapping = hasMappingWork || smartSend.isMapped;
        if (!hasAnyMapping) return undefined;
        saveTimer.current = setTimeout(() => { saveTimer.current = null; runAutoSave(); }, 750);
        return () => { if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smartSend.tokenMap, smartSend.supervisorColumnId, smartSend.gapColumnId, smartSend.sortColumnId, dirty, hasColumns, resetHold, closedHold, exitOpen]);

    // Attach the synthetic group to the campaign's GroupIds — called ONLY from a confirmed Send
    // (SendSummaryDialog.beforeSend), never on preview.
    const attachGroup = async (gid: number | null): Promise<boolean> => {

        // Attaching the synthetic group means posting the WHOLE settings row back, not a patch:
        // the server handler (NewsletterLogic.cs:1175-1193) forwards EVERY SendSettings field to
        // the stored proc, so a field missing from this body arrives as its DEFAULT and silently
        // overwrites whatever the user configured on the classic send-settings screen —
        // PulseAmount, TimeInterval, FromDate/ToDate, the IsOpened/IsOpenedClicked/IsNotClicked/
        // IsNotOpened filters, ExceptionalDays, ExeptionalCampaigns, ExeptionalGroups,
        // AutoSendingByUserField, AutoSendDelay, IsBestTime. Hence read-modify-write, with the
        // read as a HARD precondition: if it fails there is nothing to preserve, and posting a
        // minimal body "anyway" is precisely the data loss this guards against — fail the save.
        // GetSendSettings answers 201 on success (EmailController.cs:517), NOT 200 — the SmartSend
        // mock returns 200, which is exactly what would hide this until the mock is switched off.
        // Both are accepted so the screen behaves identically in mock and real mode.
        const cur: any = await dispatch(getEmailSendSettingsWrapped(campaignId));
        const curPayload = cur && cur.payload ? cur.payload : {};
        const ok = curPayload.StatusCode === 201 || curPayload.StatusCode === 200;
        const settings = ok && curPayload.Data ? curPayload.Data.Settings : null;
        // `!settings` alone would pass an [] or {} straight through (both truthy / spreadable to an
        // empty body), producing a POST with no CampaignID — worse than the minimal body this
        // replaced. Require the identifying field.
        if (!settings || !settings.CampaignID) {
            showToast(false, t('DataSources.send.errors.' + (ERR_KEY[curPayload.Message] || 'generalError')));
            return false;
        }

        // GroupIds (CSV) is the ONE field that changes — and the authoritative one: SendSettings
        // .GroupList (SendSettings.cs:35-41) is a getter-only computed property, so the GroupList
        // that came back from the GET rides along untouched and is simply ignored server-side.
        // MERGE, never replace: a combined campaign (source + regular groups, §16) keeps its
        // regular groups. Trim + de-dupe so a loose or empty CSV ('', '301,,302', '301, 302')
        // yields no empty entries and a re-save cannot append the synthetic group twice.
        const ids: string[] = String(settings.GroupIds ?? '').split(',').map((s: string) => s.trim()).filter((s: string) => s !== '');
        // A 200 with no SyntheticGroupID must never push the literal "undefined" into the CSV;
        // post the settings back unchanged rather than corrupt the group list.
        if (gid != null && Number(gid) > 0 && ids.indexOf(String(gid)) === -1) ids.push(String(gid));
        // Everything else rides verbatim — EXCEPT two scheduling fields that cannot be echoed back
        // blindly, both mirroring what the classic screen already does with the SAME payload:
        //  1. SendingMethod: 0 is a real stored value, and the classic normalises it to 1 on every
        //     post (NewsletterSendSettings.js:409). Echoing 0 writes back an invalid send method.
        //  2. A stored "scheduled" method with no SendDate is a reachable state — the classic
        //     DISABLES its save button on exactly that combination (NewsletterSendSettings.js:895-898)
        //     and the server answers 405 SEND_DATE_MISSING. Echoing it verbatim would make the
        //     mapping permanently unsavable for that campaign. Only an ALREADY-INVALID combination
        //     is normalised to "send now"; a valid schedule is preserved untouched.
        const METHOD_SCHEDULED = 2, METHOD_AUTO = 3;
        let method = settings.SendingMethod === 0 ? 1 : (settings.SendingMethod ?? 1);
        let sendDate = settings.SendDate;
        const scheduleBroken =
            (method === METHOD_SCHEDULED && !sendDate) ||
            (method === METHOD_AUTO && !sendDate && !settings.AutoSendDelay);
        if (scheduleBroken) { method = 1; sendDate = null; }
        const setRes: any = await dispatch(setEmailSendSettingsWrapped({
            ...settings, GroupIds: ids.join(','), SendingMethod: method, SendDate: sendDate,
        }));
        const s = setRes && setRes.payload ? setRes.payload : {};
        // SetSendSettings answers 201 on success (NewsletterSendSettings.js:429-431 switches on
        // exactly these codes). Anything else — 401/405/409/410/500 — means the synthetic group
        // was NOT attached, so the campaign would still send to its old recipients: that is a
        // FAILED save. It must not clear `dirty` and must not show the success toast.
        if (s.StatusCode !== 201 && s.StatusCode !== 200) {
            showToast(false, t('DataSources.send.errors.' + (ERR_KEY[s.Message] || 'generalError')));
            return false;
        }
        return true;
    };

    const openSummary = async () => {
        // item 2: persist the mapping (fillAndSummarize needs it) and stash the synthetic group
        // id, but DO NOT attach it here — the attach is deferred to the confirmed Send
        // (SendSummaryDialog.beforeSend → attachGroup). fillAndSummarize fills the staging group
        // and feeds the recipient counts the dialog shows; it does not wire the campaign, so
        // opening — and abandoning — the summary leaves GroupIds untouched (item 2 / §6).
        // KNOWN EDGE (adversarial review): if the campaign was already attached by a PRIOR send
        // whose pipeline then failed (attachGroup ok but sendSmart errored → no detach exists,
        // §6), its group is still in GroupIds, so re-filling here refreshes an already-armed
        // campaign. No worse than the pre-split behaviour; a durable fix needs a detach endpoint.
        // Flagged to the product owner.
        if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
        // 🔴 THE SAME PAIR FOR THE WHATSAPP SIDE. THE LINE ABOVE CANCELS THE E-MAIL DEBOUNCE AND
        // THE SAVE BELOW PERSISTS THE E-MAIL MAP; NEITHER HAD A WHATSAPP TWIN, ON THE ROUTE THAT
        // ENDS IN A SEND. The WhatsApp block of this dialog is read from the SERVER at :800
        // (`getWaSummary`), so an edit still inside the 750 ms debounce was not in what the
        // operator was shown — and the timer was left armed, so the save and the send raced.
        // `WhatsAppTestSendDialog.tsx:195-196` does exactly this pair before its own action; this
        // route, which sends to everyone rather than to one test number, did not.
        waAutosave.cancelPending();
        // 🔴 THE WHATSAPP FLUSH RUNS BEFORE THE E-MAIL SAVE, AND THE ORDER IS THE WHOLE POINT.
        // `cancelPending` above has just disarmed the WhatsApp debounce. If the e-mail save ran
        // first and answered non-200, the pre-existing `if (!ok) return;` below would leave this
        // function with the timer cancelled and the WhatsApp edit never written — an edit that
        // WOULD have been saved 750 ms later before this line existed. Nothing re-arms it: the
        // effect's deps (useWaAutosave.ts:151) do not include a ref mutation. Flushing first
        // means every exit from here leaves the WhatsApp map persisted.
        // Refused for the same reason `saveMapping` refuses below: the summary must not be drawn
        // over a map the server does not hold. `saveWaMapping` records the §4 code in the slice
        // (`waSaveState = 'error'`, slice:889-891), which the save indicator renders — and
        // `waNeedsSave` now carries the template id, so the one branch of `persist` that refuses
        // WITHOUT writing anything to the slice (useWaAutosave.ts:78) can no longer be reached.
        //
        // 🔴 AND IT TOASTS, BECAUSE THE E-MAIL ARM OF THIS FUNCTION DOES. `saveMapping`'s failure
        // toast (:614) is NOT gated on its `silent` argument — only the SUCCESS toast is (:625) —
        // so a failed e-mail save from here already speaks. Leaving the WhatsApp arm silent would
        // make one button behave two ways on the same click, and the save indicator alone is a
        // thin signal for a refusal that has just cancelled a send. `waErrorText` (:209) is the
        // house helper for turning a §4 code into a sentence; it falls back to WA_INTERNAL
        // through `i18n.exists`, so an unknown code can never render as a raw key, and it needs
        // no new i18n keys.
        // 09-channel-units — THE WHATSAPP UNIT IS SAVED BEFORE EVERY SUMMARY, exactly as the e-mail
        // unit is (`saveMapping(true)` below runs unconditionally). The save carries the source ON
        // SCREEN, so the server re-binds the unit's own row when it points elsewhere — "both" must
        // reach ONE audience, and the send refuses two (WA_SOURCE_MISMATCH); a variable still on the
        // old source's column is then refused here (-9) and shown instead of being sent. It also
        // writes the unit's variable count from the approved template (a unit made by an older
        // bundle may still hold NULL, which the send refuses). One idempotent request; a unit that
        // may not be written (`canWrite` false) posts nothing, as everywhere else.
        const waUnitWritable = waUnitId != null && waActive && !!waTemplateId && waAutosave.canWrite;
        if (waNeedsSave || waUnitWritable) {
            const wa = await waAutosave.saveWaMapping();
            if (!wa.ok) {
                // 12-fix-wave 7.4(d) — these two answer through the page (the dialog / "send closed"), not a toast here.
                if (wa.message !== 'SOURCE_RESET_CONFIRM_REQUIRED' && wa.message !== 'SEND_ALREADY_SENT') {
                    showToast(false, waErrorText(wa.message));
                }
                return;
            }
        }
        const { ok, gid } = await saveMapping(true);
        if (!ok) return;
        setPendingGid(gid);
        // ══ 12-fix-wave 7.4(a) — THE SUMMARY SHOWS WHAT THE SERVER WILL SEND ═════════════════════════
        // The send's row is read again BEFORE the summaries load: the WhatsApp part is shown if and only
        // if the fresh `RowWACampaignID` is set, and when it differs from what the page carried the page
        // moves to it first. `readMappingRow` updates the row and nothing else on screen. A read that
        // fails opens no summary.
        const fresh: any = await dispatch(readMappingRow({ campaignId, channel: pageChannel }));
        const fd: any = fresh?.payload?.StatusCode === 200 ? fresh.payload.Data : null;
        if (!fd) { showToast(false, t('DataSources.send.errors.generalError')); return; }
        // A unit's own page whose row now holds an e-mail is a "both" send: the redirect effect moves
        // the page to the e-mail's page, where the send is made.
        if (isWaPage && fd.RowCampaignID != null) return;
        const freshWa: number | null = isWaPage
            ? waUnitId
            : (fd.RowWACampaignID != null && isUnitId(Number(fd.RowWACampaignID)) ? Number(fd.RowWACampaignID) : null);
        if (!isWaPage && freshWa !== waUnitId) {
            dispatch(setWaUnit({ unitId: freshWa, unitMode: freshWa != null ? 'both' : 'email' }));
            if (freshWa != null) await dispatch(getWaMapping({ campaignId: freshWa, channel: 3 }));
        }
        // 🔴 ALL THREE ARE E-MAIL ROUTES AND CHANNEL 3 HAS NO E-MAIL CAMPAIGN.
        // dbo.DataSources_FillCampaignGroup answers -9 for any channel but 1 by design — its job
        // is to build a synthetic RECIPIENT GROUP and attach it to an e-mail campaign, and the
        // WhatsApp sender does not use groups at all (it reads the locked version's rows). The
        // other two read dbo.Campaigns by id. For a stand-alone container the summary that
        // matters is the WhatsApp one below, and it is fetched unconditionally in that state.
        const waOnlyChannel = Number(urlChannel) === 3;
        if (!waOnlyChannel) {
            await dispatch(fillAndSummarize({ campaignId, channel: smartSend.selectedChannel }));
            await dispatch(getSendSummaryWrapped(campaignId));
            await dispatch(getEmailSendSettingsWrapped(campaignId));
        }
        // The WhatsApp block of the dialog. A failure here does NOT open the dialog on a lie:
        // the slice records the §4 `Message`, the dialog renders `error.<Message>` and disables
        // Send, rather than falling through to an e-mail-only send in silence (14-W3 §1).
        // 09-channel-units — the WhatsApp unit's OWN summary, by its own id on channel 3, WHENEVER the
        // page carries a unit — never gated on `waActive`: the dialog holds Send until it has this
        // summary (`SendSummaryDialog` `waUnitHeld`), so a "both" page whose Availability answered
        // late can never send without the counters and warnings the operator is shown. An
        // Availability read that FAILED is asked again first, so "try again" can succeed.
        if (freshWa != null) {
            if (waAvailabilityStatus === 'failed') await dispatch(getWaAvailability());
            await dispatch(getWaSummary({ campaignId: freshWa, channel: 3 }));
        }
        setSummaryOpen(true);
    };

    // LOAD-BEARING — DO NOT DELETE. `openSummary` saves UNCONDITIONALLY (no `dirty` check), and
    // `canSend` is true on `hasColumns` alone whenever nothing is unmapped — which includes a
    // template with zero tokens. So "שלח לכולם" is a SECOND point of no return, and a faster one
    // than the 750ms debounce: one click writes an empty mapping under the new source and the SP
    // delete-and-reinserts the token map, destroying the saved mapping. Without this gate the
    // restore banner would be on screen asserting that mapping still exists at the exact moment a
    // single click removes it — the screen contradicting itself about the only thing that matters.
    const requestSummary = () => {
        if (showRestore && !hasMappingWork) { setReplaceConfirmOpen(true); return; }
        openSummary();
    };

    // ── restore the campaign's SAVED mapping ───────────────────────────────────────────────────
    // Re-runs the mount effect's own pair (setCampaignContext + getMapping) — the exact code path
    // F5 takes, minus the reload. It reads the SERVER; nothing is replayed from memory, so this
    // can never resurrect something the server would refuse, and it is structurally incapable of
    // becoming a send payload.
    //
    // setCampaignContext is load-bearing, not tidiness: getMapping.fulfilled does not write
    // state.dataSourceId, so without it the store keeps the abandoned source's id and
    // SourcePicker's `incoming` banner pops back up on top of the restored screen.
    const restoreSaved = async () => {
        // LOAD-BEARING — DO NOT DELETE. An autosave timer armed moments ago would otherwise fire
        // AFTER this click and post the NEW source with Mappings=[] — and
        // CampaignsToDataSources_Set delete-and-reinserts the token map, so it would wipe the very
        // mapping this button just restored, while the sticky bar painted "✓ נשמר" over it. The
        // recovery button would destroy the thing it promises to recover. openSummary already
        // clears the timer for exactly this reason.
        if (saveInFlight.current || saving) return;
        if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
        savePending.current = false;
        setDirty(false);
        // 'locked' is a latched fact about the campaign, not about this screen's edit state — it
        // must survive. Anything else is stale the moment the server's mapping is back on screen.
        setSaveState((s) => (s === 'locked' ? s : 'idle'));
        setRestoring(true);
        setRestoreFailed(false);
        dispatch(setCampaignContext({ campaignId, dataSourceId: parsedDsId }));
        const res: any = await dispatch(getMapping({ campaignId, channel: smartSend.selectedChannel }));
        setRestoring(false);
        if (res?.payload?.StatusCode !== 200) setRestoreFailed(true);
    };

    // The sticky bar must not carry a verdict from the previous source across a switch — a green
    // "כל השינויים נשמרו" over a freshly emptied table is the screen asserting something false.
    // 'error' and 'locked' are deliberately preserved: see the failedSaveSourceId comment above.
    useEffect(() => {
        setSaveState((s) => (s === 'error' || s === 'locked') ? s : 'idle');
    }, [currentSourceId]);

    // item 3: leaving the loaded screen (the 404 branch has nothing to lose; the summary-close
    // redirect runs only after a real send). 09-channel-units — WHERE to is `exitTarget`: the picker
    // by default, the WhatsApp unit's own page when the e-mail switch is turned off, the picker
    // without the source for "change source".
    // 12-fix-wave 7.4(b) — «הסר» leaves only after POST Leave answered 200. On a refusal the page says
    // why and stays (409 SEND_ALREADY_SENT → "send closed"). After the WhatsApp «הסר» the page stays on
    // the same route (the e-mail's), so its row is read again; the e-mail «הסר» opens another page.
    const runLeave = async (): Promise<boolean> => {
        if (waUnitId == null) return false;
        const res: any = await dispatch(leaveSend({ campaignId, waCampaignId: waUnitId }));
        const p = res && res.payload ? res.payload : {};
        if (p.StatusCode === 200) return true;
        showToast(false, p.Message === 'SEND_ALREADY_SENT'
            ? t('DataSources.send.errors.sendClosed')
            : t('DataSources.send.errors.' + (ERR_KEY[p.Message] || 'generalError')));
        return false;
    };

    const doExit = async () => {
        // One Leave at a time: a second click while POST Leave is on the wire does nothing.
        if (leavingRef.current) return;
        const url = exitTarget.current ?? `${sitePrefix}SmartSend${parsedDsId != null ? `?dataSourceId=${parsedDsId}` : ''}`;
        if (pendingLeave.current) {
            pendingLeave.current = false;
            leavingRef.current = true;
            const left = await runLeave();
            leavingRef.current = false;
            if (!left) return;
            if (url === emailUnitUrl) dispatch(getMapping({ campaignId, channel: pageChannel }));
        }
        // QA BUG-32 — leaving now: the entry check must not run any more (see `exitingRef`).
        exitingRef.current = true;
        // 12-fix-wave 7.4(d) — the reset confirmation is armed HERE, after the exit dialog's saves and
        // just before leaving for the picker, so no save of this page (on the unchanged source) uses it up.
        if (armResetOnExit.current) {
            armResetOnExit.current = false;
            // An armed debounce would otherwise fire after this and carry the confirmation on the old source.
            stopAutosaveTimers();
            armConfirmReset(pageChannel, campaignId);
            // QA BUG-31 r1 — tells the picker's next entry that this is the change-source trip (it keeps
            // the WhatsApp intent then). Set HERE, on the actual leave, not when the dialog opens: a
            // cancelled exit dialog must not leave a marker behind.
            markChangeSourceTrip();
        }
        Redirect({ url, openNewTab: false });
    };

    // 🔴 2026-09-23 — THE SOURCE IS SETTLED WHEN THE URL SAYS SO, AND ONLY THEN.
    // `parsedDsId` (:76) is the sanitised `?dataSourceId=`; a garbage param is already null
    // there, so a malformed URL falls back to the picker rather than locking a heading over
    // nothing. This deliberately does NOT read the slice: `s.smartSend.dataSourceId` survives
    // the whole SPA session (`clearSmartSend` has zero dispatch sites), so a source left behind
    // by a previous campaign would lock this screen over a choice the operator never made here.
    const sourceSettled = parsedDsId != null;

    // The escape hatch out of the locked heading: back to the picker with the source DROPPED,
    // which is the whole point of the button. It goes through the same unsaved-work gate as the
    // back link — changing the source is exactly as destructive as leaving, and `restoreSaved`'s
    // note above explains what an armed autosave does to a mapping whose source just moved.
    // 🔴 `|| waNeedsSave` (:299) — ON A WHATSAPP-ONLY SEND `dirty` IS FALSE FOR THE WHOLE SESSION.
    // This gate read it alone, so the one channel that had unsaved work was the one channel that
    // got no dialog: `doExit` navigates, the component unmounts, and the debounce cleanup at
    // useWaAutosave.ts:149 clears the armed timer WITHOUT persisting. The edit left with the
    // screen, silently, inside the 750 ms window.
    // 09-channel-units — ONE gate for every way out; each caller only names where "out" is.
    const leaveWithGate = () => { if (dirty || waNeedsSave) { setExitSaveFailed(false); setExitOpen(true); } else doExit(); };
    // The source DROPPED — the whole point of that button (and before the units, a save-and-exit
    // from its dialog went back to the picker WITH the source, which is not what was asked for).
    // 12-fix-wave 7.4(d) — when the send's row exists (the last read has a DataSourceID), the reset
    // dialog comes BEFORE leaving to the picker; no row (a first mapping) ⇒ no dialog. A closed send
    // cannot get here: its «החלף מקור» is disabled (`sendIsClosed`).
    // QA BUG-29 (K-01) — on the way back from the picker the entry check above sees the '1' key armed
    // below and switches to the picked source without asking again.
    const leaveToPicker = (armReset = false) => {
        exitTarget.current = `${sitePrefix}SmartSend`;
        pendingLeave.current = false;
        armResetOnExit.current = armReset;
        leaveWithGate();
    };
    const goChangeSource = () => {
        if (sendIsClosed) return;
        if (mappingRow?.dataSourceId != null) { setResetDlg('changeSource'); return; }
        leaveToPicker();
    };
    const handleBackToPicker = () => { exitTarget.current = null; pendingLeave.current = false; armResetOnExit.current = false; leaveWithGate(); };
    // Save & exit must NOT navigate on a failed save (saveMapping returns ok:false on StatusCode
    // != 200); navigating then would silently drop the very changes the user asked to keep.
    //
    // 🔴 AND IT MUST CARRY BOTH SIDES, OR WIDENING THE GATE ABOVE MAKES THIS SCREEN WORSE THAN
    // IT WAS. `saveMapping` writes the ARC ROW through `setMapping` (:577) — one row in
    // dbo.CampaignsToDataSources. The variable map is a different table behind a different route,
    // reached only through `setWaMapping`, whose one dispatcher is useWaAutosave.ts:106. With the
    // gate widened and this left alone the dialog would open, the operator would press
    // "שמור וצא", `saveMapping` would answer 200, the success toast would paint "המיפוי נשמר" —
    // and the WhatsApp work would still be thrown away, now with the screen asserting it was
    // kept. Silent loss is bad; an affirmed save that did not happen is worse.
    const handleSaveAndExit = async () => {
        // The debounce must not fire against the state we are about to post by hand. Same pair,
        // same order, as WhatsAppTestSendDialog.tsx:195-196 — the one place on this screen that
        // already gets this right. It cancels a PENDING timer, not a request already in flight;
        // in the scenario this fix exists for (edit, then leave inside 750 ms) there is no
        // in-flight request, because the timer never fired.
        // QA BUG-10 — BOTH timers (and the e-mail trailing flag), not only the WhatsApp one: an armed
        // e-mail debounce here was the second SetMapping of "שמור וצא".
        stopAutosaveTimers();
        const { ok, code } = await saveMapping();
        // 12-fix-wave 7.4(d) — the reset dialog has taken over (saveMapping closed this one).
        if (code === 'SOURCE_RESET_CONFIRM_REQUIRED') return;
        // 🔴 CONDITIONAL, AND NOT AS AN OPTIMISATION. `saveWaMapping` answers
        // `{ ok: false, 'WA_TEMPLATE_NOT_APPROVED' }` whenever there is no template id
        // (useWaAutosave.ts:77-79), which is every ordinary e-mail campaign. Calling it
        // unconditionally would fail the exit on all of them — a screen that cannot be left by
        // the button that exists to leave it.
        // Both saves are ATTEMPTED even when the first fails: persisting the WhatsApp work is
        // strictly better than dropping it because the e-mail save happened to answer 409.
        // `{ ok: true, message: null }` and not `{ ok: true }`: both arms then carry the hook's
        // declared shape, so the inferred union cannot drift if a reader ever wants `message`.
        // 12-fix-wave 7.4(c) — NO WhatsApp save after a failed e-mail save.
        const wa = ok && waNeedsSave ? await waAutosave.saveWaMapping() : { ok: true, message: null };
        if (ok && wa.ok) { setExitOpen(false); doExit(); } else setExitSaveFailed(true);
    };

    // `!sent`: once sendSmart has answered 200/201 for this campaign the screen must not be able
    // to arm a second send. Closing the summary dialog navigates away, but this gate deliberately
    // does not depend on that navigation actually happening.
    // `canSend = emailCanSend && (!waActive || selectWaCanSend)` (contract §6). A page whose
    // WhatsApp unit has not been read yet must not quietly degrade into an e-mail-only send.
    // On the WhatsApp unit's own page ActionBar names it beside the disabled button with
    // `mapping.waOnlySetupIncomplete` (C-29); a "both" page shows no hint until the unit's GET answers.
    // The same sentence names a still-unmapped WhatsApp variable (QA BUG-13).
    const emailCanSend = hasColumns && !sent && (unmapped.length === 0 || confirmUnmapped);
    // 🔴 v1.23 — ON A WHATSAPP-ONLY CAMPAIGN THE E-MAIL'S TOKENS DO NOT GATE THE SEND.
    // `emailCanSend` carries "every ##token## is mapped, or the operator confirmed the gaps" —
    // a condition about a mail that this campaign will not send. Keeping it would have made the
    // whole mode unusable for any campaign whose body has an unmapped token, and it would have
    // asked the operator to confirm a shortfall with no consequence. What survives is what is
    // still true: a source must be loaded, and the campaign must not already have been sent.
    // 🔴 C-29 — AND IN THE WHATSAPP-ONLY BRANCH `waCanSend` IS THE GATE THAT MATTERS.
    // It requires `HasLeg ∧ HasHeader` — both 1 once the unit's own GET (channel 3) has answered —
    // so until the unit is read the button is refused, and `ActionBar` renders
    // `selectWaSetupIncomplete` as the reason. (09-channel-units: this page sends through
    // `PUT WhatsAppOnly/Send/{unit}`, chosen by `?channel=3`, so it can never fall through to an
    // e-mail send.)
    const canSend = waOnly
        ? (hasColumns && !sent && waCanSend)
        : (emailCanSend && (!waActive || waCanSend));
    // The tab the SCREEN is on, as opposed to the one the (possibly absent) strip last set.
    // With one channel there is no strip, so `activeTab` is whatever it was when the strip was
    // last on screen — and it drives the test-send target. Sending an E-MAIL test from a
    // WhatsApp-only campaign is the kind of wrong that only shows up in someone's inbox.
    const effectiveTab: WaTabKey = waOnly ? 'whatsapp' : activeTab;

    // renderBody's OWN two guards, as one value: the body renders its blocks exactly when this holds.
    // The header's source line mounts SourcePicker on the same condition, so that component — which
    // fetches the source list and preselects `?dataSourceId` on mount — still mounts once, at the
    // moment it always did, whether it renders in the header (a settled source) or in the body.
    const bodyReady = !!campaignId && !Number.isNaN(campaignId) && campaignId > 0
        && (restoring || restoreFailed || smartSend.mappingStatus === 'succeeded');

    // 2026-09-28 (owner, stage) — ON THE WHATSAPP UNIT'S OWN PAGE THE PHONE STANDS BESIDE THE WHOLE
    // PAGE, from the header down, not below it. That page is the WhatsApp tab and nothing else, so the
    // column beside the header is empty space the phone can use (the owner's own mock-up: everything
    // raised, the text centred above the phone). On a "both" page the tab strip spans the width and the
    // e-mail tab has no phone, so there the phone stays inside the WhatsApp tab.
    // On renderBody's own condition, like the tab whose preview it takes over — so it mounts when the
    // tab would have mounted it.
    const pagePreview = unitMode === 'whatsapp' && waActive && waUnitId != null && bodyReady;
    // Pinned while the mapping scrolls — only in the two-column layout and on a screen tall enough to
    // hold the phone (same rule as the tab's own column, WhatsAppMappingTab `previewCol`).
    const pinPreview = useMediaQuery('(min-width:960px) and (min-height:760px)');

    const renderBody = () => {
        if (!campaignId || Number.isNaN(campaignId) || campaignId <= 0) {
            // QA BUG-14 (B-14) — the same "not found" body as the server's 404 below, with its way back.
            return (
                <Box>
                    <Typography style={{ marginBottom: 12 }}>{t('DataSources.send.errors.notFound')}</Typography>
                    <Button variant="outlined" color="primary" onClick={() => Redirect({ url: `${sitePrefix}Campaigns`, openNewTab: false })}>
                        {t('DataSources.send.backToCampaigns')}
                    </Button>
                </Box>
            );
        }
        // LOAD-BEARING — DO NOT DELETE THE `!restoreOverlay` GUARDS ON THESE TWO RETURNS.
        // getMapping.pending sets mappingStatus='loading', so a restore click would otherwise
        // replace the whole body — including the restore button, its spinner and its own error
        // line — with a bare progress bar: the fix for "everything vanished" would itself make
        // everything vanish, and a FAILED restore would dead-end the screen with the working state
        // unreachable. A failed getMapping writes only mappingStatus/mappingError — tokens, columns
        // and tokenMap all survive — so the previous body is still renderable underneath.
        const restoreOverlay = restoring || restoreFailed;
        if (!restoreOverlay && (smartSend.mappingStatus === 'loading' || smartSend.mappingStatus === 'idle')) return <LinearProgress />;
        // QA BUG-32 — nothing to edit or send while the entry check has not decided (see `entryPending`).
        if (!restoreOverlay && entryPending) return <LinearProgress />;
        if (!restoreOverlay && smartSend.mappingStatus === 'failed') {
            // §16: distinct UI per code. 404 (foreign/deleted campaign) → back to the list;
            // 927 (feature off) → its own message; everything else → generic retry text.
            if (smartSend.mappingError === 404) {
                return (
                    <Box>
                        <Typography style={{ marginBottom: 12 }}>{t('DataSources.send.errors.notFound')}</Typography>
                        <Button variant="outlined" color="primary" onClick={() => Redirect({ url: `${sitePrefix}Campaigns`, openNewTab: false })}>
                            {t('DataSources.send.backToCampaigns')}
                        </Button>
                    </Box>
                );
            }
            // 927 (feature off) and the generic branch were both bare text: the screen has no
            // other control on it in this state, so a transient failure — or an entitlement that
            // was switched on server-side moments ago — left the user with a dead end and no way
            // out but the browser. Both now re-run the only request this screen loads with.
            const retry = (
                <Button variant="outlined" color="primary" size="small" startIcon={<Refresh />}
                    onClick={() => dispatch(getMapping({ campaignId, channel: smartSend.selectedChannel }))}>
                    {t('DataSources.send.errors.retryAction')}
                </Button>
            );
            if (smartSend.mappingError === 927) {
                return (
                    <Box>
                        <Typography style={{ marginBottom: 12 }}>{t('DataSources.send.errors.featureOff')}</Typography>
                        {retry}
                    </Box>
                );
            }
            return (
                <Box>
                    <Typography style={{ marginBottom: 12 }}>{t('DataSources.send.errors.generalError')}</Typography>
                    {retry}
                </Box>
            );
        }

        // The e-mail blocks, EXACTLY as they were. In tabs mode they become the first panel;
        // otherwise they are the whole body. Extracted as a function rather than duplicated so
        // the e-mail-only path cannot drift from the tabbed one (14-W3 §1: `renderEmailBlocks()`
        // = today's body, unchanged).
        // 🔴 `withSource` — AND C-27 IS THE CORRECTION TO WHAT THIS COMMENT USED TO CLAIM.
        //
        // It said `SourcePicker` must be visible in every channel state and then hoisted it
        // ONLY in WhatsApp-only mode, because that was the state where the e-mail panel is
        // permanently hidden. The rule was right and the implementation answered a narrower
        // question: in the BOTH-channels state the panel is hidden too — whenever the operator
        // is on the WhatsApp tab — and the picker vanished with it. The campaign-to-source link
        // disappeared from the tab that also needs a source and a column mapping.
        // ⚠️ A comment that states the correct rule next to code that implements a narrower one
        // is worse than no comment: it is what stopped this being seen for two rounds.
        //
        // So the picker now leaves this function in EVERY tabbed state (`false` from the only
        // caller that renders panels) and is rendered once, above them.
        // `true` remains the default for the ONE path with no panels at all — the add-on off,
        // where the body is this function's output and nothing else. There the grid keeps its
        // original position for a page that still has to CHOOSE a source; a SETTLED source is named
        // in the page header on every page (owner, stage 2026-09-28 — which ends the add-on-off
        // page's pixel-identity, 14-W3 §2, for that one line by his request).
        //
        // ⚠️ And it is a PARAMETER rather than a second `<SourcePicker/>` because two mounts
        // would mean two `getDataSources` fetches, two radiogroups with the same label, and two
        // DOM nodes sharing every `smartsend-src-desc-<id>` id. Exactly one mount, always.
        const renderEmailBlocks = (withSource: boolean = true) => (
            <Box>
                {/* The e-mail of this campaign has already gone out. The text is
                    `mapping.emailLockedAfterEmailSent` and NOT `mapping.lockedAfterSend`: the
                    latter says "the WhatsApp has already been sent", which is false in states
                    (1) and (3) — exactly the states the whole late-send path exists for
                    (v1.13 AC-02). */}
                {emailLocked && (
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        {t('DataSources.send.whatsapp.mapping.emailLockedAfterEmailSent')}
                    </Typography>
                )}
                <StaleVersionBanner />
                <MappingMismatchBanner />
                {/* WHAT THE SERVER HOLDS — stated from `savedMapping`, never from working state.
                    This line used to read `isMapped ? mappedTo : notMapped`, and since selectSource
                    clears isMapped it announced "הקמפיין עדיין לא ממופה למקור נתונים" over a
                    campaign whose mapping was sitting intact in the database. That single false
                    sentence is what made a source switch read as "everything vanished".
                    Three states, each true: drafting elsewhere / showing the saved mapping / no
                    saved mapping at all. The last renders only once the load has actually
                    succeeded, so an in-flight or failed read stays silent rather than optimistic.
                    aria-live so the switch and the restore are both announced, not just painted. */}
                <Typography variant="body1" style={{ marginBottom: 8 }} aria-live="polite">
                    {saved && !smartSend.workingIsFromServer
                        ? t('DataSources.send.source.savedLineDraft', {
                            name: saved.dataSourceName ?? '',
                            mapped: (saved.mappedTokenCount ?? 0).toLocaleString(),
                            total: (saved.tokenCount ?? 0).toLocaleString() })
                        : saved
                            ? t('DataSources.send.source.savedLine', {
                                name: saved.dataSourceName ?? '',
                                mapped: (saved.mappedTokenCount ?? 0).toLocaleString(),
                                total: (saved.tokenCount ?? 0).toLocaleString() })
                            : smartSend.mappingStatus === 'succeeded'
                                ? t('DataSources.send.source.savedNone')
                                : null}
                </Typography>
                {/* THE RECOVERY. Self-invalidating by construction: workingIsFromServer flips true
                    on the next successful save or restore, which is precisely the moment the
                    server's copy of the old mapping stops existing. The banner therefore cannot
                    outlive what it promises. */}
                {showRestore && saved && (
                    <Box style={{ marginBottom: 8 }}>
                        <InlineBanner
                            severity="warning"
                            role="status"
                            title={t('DataSources.send.source.restoreTitle')}
                            body={t('DataSources.send.source.restoreBody', { name: saved.dataSourceName ?? '' })}
                            action={(
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    disabled={restoring || saving || saveState === 'saving'}
                                    onClick={restoreSaved}
                                >
                                    {restoring
                                        ? <CircularProgress size={16} />
                                        : t('DataSources.send.source.restoreAction')}
                                </Button>
                            )}
                        />
                        {restoreFailed && (
                            <Typography variant="body2" color="error" style={{ marginTop: 6 }}>
                                {t('DataSources.send.source.restoreFailed')}
                            </Typography>
                        )}
                    </Box>
                )}
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    {t('DataSources.send.tokensFound', { count: smartSend.tokens.length.toLocaleString() })}
                </Typography>

                {/* ⚠️ CORRECTED 2026-09-22. Three assertions stood here and all three were false
                    against the code in this file: that the channel is "no longer picked here";
                    that `clearSmartSend` lives at smartSendSlice.ts:316; and that EMAIL is the
                    only enabled channel. Superseded by the effect 900 lines above.

                    THE CHANNEL IS PICKED HERE, and that is what closes the sticky-value bug.
                    :99-104 derives it from the URL on every [campaignId, urlChannel] change —
                    `?channel=3` when the picker hands over a WhatsApp container id, and ABSENCE
                    means e-mail as a positive answer, not "whatever was chosen last". So a
                    direct landing (campaign-row icon, BEE editor button, pasted URL) re-derives
                    the channel instead of inheriting it.

                    `clearSmartSend` is at smartSendSlice.ts:566, exported at :873, and does have
                    ZERO dispatch sites — measured across src, only the definition, the export and
                    comments referring to it. The slice therefore still outlives a
                    campaign-to-campaign navigation; what protects this screen is the re-derivation
                    above, not a reset.

                    And EMAIL is NOT the only enabled channel: `CHANNELS` in
                    Models/DataSources/SmartSend.ts marks EMAIL and WHATSAPP `enabled: true` and
                    only SMS `enabled: false`. The day SMS flips, the effect above already handles
                    it — it tests `c.enabled`, not a hardcoded channel. */}
                {/* A SETTLED source is named in the page header (beside the title), not here — the
                    grid below is only for a page that still has to choose one. */}
                {withSource && !sourceSettled && <SourcePicker readOnly={emailLocked} onChangeSource={goChangeSource} changeDisabled={sendIsClosed} unsaved={dirty || waNeedsSave} />}

                {/* Source-column load (after a pick) — loading spinner + error/retry so a
                    failed getDataSource never silently strands the user with no mapping table. */}
                {!hasColumns && currentSourceId != null && smartSend.columnsStatus === 'loading' && (
                    <Box style={{ marginTop: 20 }}><CircularProgress size={22} /></Box>
                )}
                {!hasColumns && currentSourceId != null && smartSend.columnsStatus === 'failed' && (
                    <Box style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Typography color="error">{t('DataSources.send.source.loadError')}</Typography>
                        <Button size="small" startIcon={<Refresh />} onClick={() => dispatch(loadSourceColumns(currentSourceId as number))}>
                            {t('DataSources.retry')}
                        </Button>
                    </Box>
                )}

                {hasColumns && (
                    <>
                        <TokenMappingTable
                            tokens={smartSend.tokens}
                            columns={smartSend.columns}
                            value={smartSend.tokenMap}
                            onChange={(token, columnId) => { dispatch(setTokenMapping({ token, columnId })); markEdited(); }}
                            warnSystemFieldOverride
                            /* Invariant 24(3): `readOnly` means ZERO onChange from the component
                               — the Select, the static field AND the suggestion chip, which is
                               neither of the first two and stayed live under the older
                               "disable the Select/TextField" wording (v1.12 AB-01). */
                            readOnly={emailLocked}
                            readOnlyReason={sendClosedReason}
                            /* 08-mapping-memory: memory FIRST, then — through the opt-in, and
                               only here — the name-similarity chip for a token memory has
                               nothing for. The WhatsApp tab never passes `fallbackToSimilarity`. */
                            suggestionSource={emailMemorySource}
                            fallbackToSimilarity
                            /* 2026-09-28 — the header's one click for every "used last time" chip.
                               Memory ONLY: the similarity fallback stays one row at a time. */
                            bulkSuggestionSource={emailMemorySource}
                        />
                        {/* Gap + Sort are ONE control now; `setBusinessColumn('gapSort')` writes the
                            same ColumnID into both slice fields, so buildSaveRequest keeps sending
                            both server fields and no API/SP change was needed. `stored*` are the raw
                            server values, used only to warn when a pre-merge mapping held two
                            different columns. */}
                        <BusinessColumnsPicker
                            columns={smartSend.columns}
                            supervisorColumnId={smartSend.supervisorColumnId}
                            gapColumnId={smartSend.gapColumnId}
                            storedGapColumnId={smartSend.storedGapColumnId}
                            storedSortColumnId={smartSend.storedSortColumnId}
                            onChange={(role, columnId) => { dispatch(setBusinessColumn({ role, columnId })); markEdited(); }}
                            supervisorEnabled
                            /* A guessed supervisor column is posted as NULL by buildSaveRequest
                               above, so while the guess stands the shortfall column provably has no
                               effect — the picker disables itself and hides its chip rather than
                               offering a control that cannot change what gets saved. */
                            supervisorColumnIsGuess={smartSend.supervisorColumnIsGuess}
                            readOnly={emailLocked}
                            readOnlyReason={sendClosedReason}
                        />

                        <Box style={{ marginTop: 24 }}>
                            <UnmappedTokensWarning tokens={unmapped} confirmed={confirmUnmapped} onConfirmChange={setConfirmUnmapped} />
                        </Box>

                        {/* wizard actions. Save moved to the sticky save bar (shown only when
                            there are unsaved changes), co-located with the unsaved indicator.
                            In TABS mode these three move into the sticky ActionBar at the bottom
                            of the screen, so they are reachable from both tabs; with the add-on
                            off this row is exactly what it always was. */}
                        {!waActive && (
                            <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20, borderTop: '1px solid var(--pls-d-border, #e0e0e0)', paddingTop: 18 }}>
                                <Button variant="outlined" color="primary" onClick={() => setShowPreview((v) => !v)}>
                                    {t('DataSources.send.actions.preview')}
                                </Button>
                                <Button variant="outlined" color="primary" onClick={() => setTestOpen(true)}>
                                    {t('DataSources.send.actions.testSend')}
                                </Button>
                                {/* requestSummary, NOT openSummary — see the guard's comment: this
                                    button is a second and faster point of no return than the debounce. */}
                                <Button variant="contained" color="primary" disabled={!canSend || saving} onClick={requestSummary}>
                                    {t('DataSources.send.actions.sendToAll')}
                                </Button>
                            </Box>
                        )}

                        {/* Preview moved from an inline block into a dialog. Same SmartSendPreview,
                            same campaignId — only the container changed. The toggle button above opens
                            it; onClose (backdrop / Esc / close button) closes it. Shared with the send summary. */}
                        <SmartSendPreviewDialog open={showPreview} campaignId={campaignId} onClose={() => setShowPreview(false)} />
                    </>
                )}
            </Box>
        );

        if (!waActive) return renderEmailBlocks();

        return (
            <Box>
                {/* §6: "לשוניות רק כששני הערוצים דלוקים; אחרת מסך אחד." A strip with one usable
                    tab is not navigation, it is decoration that costs a click. */}
                {/* The strip exists on a "both" page only (`selectShowChannelTabs`), and so does
                    "הסר" beside each tab: it replaces the channel tiles this page used to repeat
                    from the picker (09-channel-units — the channels ARE the page's units). */}
                {showTabs && (
                    <ChannelTabs
                        value={activeTab}
                        onChange={setActiveTab}
                        emailMapped={smartSend.tokens.length - unmapped.length}
                        emailTotal={smartSend.tokens.length}
                        removeEmail={removeEmailAction}
                        removeWhatsApp={removeWaAction}
                    />
                )}
                {/* 🔴 THE SOURCE PICKER, HOISTED — IN EVERY CHANNEL STATE THAT HAS PANELS, while the
                    page still has to CHOOSE a source. §6: "ו-`SourcePicker` גלוי בכל המצבים — הוא
                    הקישור למקור, לא חלק מלשונית המייל." Inside the e-mail panel it would vanish the
                    moment the operator opens the WhatsApp tab. A settled source is named in the
                    page header instead — still one mount, never two.
                    ⚠️ NO `waOnly` CONDITION, and no second copy inside the panel below
                    (`renderEmailBlocks(false)`). */}
                {!sourceSettled && <SourcePicker readOnly={emailLocked} onChangeSource={goChangeSource} changeDisabled={sendIsClosed} unsaved={dirty || waNeedsSave} />}
                {/* ⚠️ BOTH PANELS STAY MOUNTED and one is hidden, rather than unmounting the
                    inactive one — including in WhatsApp-only mode, where the e-mail panel is
                    hidden for the whole session. The e-mail side owns `dirty`, the 750 ms
                    debounce and the restore banner; unmounting it mid-edit drops an ARMED
                    autosave on the floor, and that is the one failure this arrangement exists to
                    prevent (§6: "אל תפרק אותם, אחרת autosave חמוש נופל").
                    `hidden` removes the subtree from the accessibility tree and the tab order,
                    so nothing in there is reachable while it is off. */}
                <Box hidden={!showTabs || activeTab !== 'email'} role="tabpanel">
                    {renderEmailBlocks(false)}
                </Box>
                <Box hidden={showTabs && activeTab !== 'whatsapp'} role="tabpanel">
                    {/* 09-channel-units — the WhatsApp UNIT's own id; the tab speaks only to it. */}
                    <WhatsAppMappingTab campaignId={waUnitId ?? 0} classes={classes} autosave={waAutosave}
                        reused={searchParams.get('reused') === '1'} showPreview={!pagePreview}
                        onLeave={removeWaAction ? removeWaAction.onRemove : null} closedReason={sendClosedReason} />
                </Box>
            </Box>
        );
    };

    return (
        // subPage drives BOTH the side-menu highlight (SideBarItem.tsx:256 matches
        // `option.key === subPage`) and the document title (DefaultScreen.js:41-43 looks the
        // option up by key inside the `groups` route). This screen's own route entry is
        // `smartSend` (routes.tsx), so `dataSources` highlighted the wrong sibling and titled
        // the page "Data Sources" for every user who continued from the picker.
        <DefaultScreen currentPage="groups" subPage="smartSend" classes={classes} containerClass={clsx(classes.management, classes.mb50)}>
            {/* paddingBottom reserves clearance so the sticky save bar (below) never rests over
                the "Send to all" button / last action row (contract §3). */}
            <Box style={{ padding: 16, paddingBottom: 88 }}>
                {/* The WhatsApp unit's own page (`pagePreview`): the header and the body share one
                    column and the phone has its own beside them, from the top — about 7 : 5, the
                    split the tab's own grid uses, with the phone centred in its column. Every other
                    page: the same two elements, unsplit. Wraps below 924px, so a narrow screen still
                    gets the phone under the mapping. */}
                <Box style={pagePreview ? { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24 } : undefined}>
                    <Box style={pagePreview ? { flex: '7 1 560px', minWidth: 0 } : undefined}>
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                            <Box>
                                {/* The settled source sits BESIDE the title, in smaller type: its name,
                                    a link that opens it, and "החלף מקור". A page that still has to
                                    choose a source shows the grid in the body instead (`!sourceSettled`). */}
                                <Box style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
                                    <Typography variant="h5">{t('DataSources.send.title')}</Typography>
                                    {sourceSettled && bodyReady && (
                                        <SourcePicker inline locked readOnly={emailLocked} onChangeSource={goChangeSource} changeDisabled={sendIsClosed} />
                                    )}
                                </Box>
                                {/* Which campaign is about to go out. On entry A (a source tile → this
                                    screen) the title alone identified nothing, so the user could confirm a
                                    send to every recipient in the source without ever seeing the campaign
                                    name. Absent (or belonging to another campaign) → render nothing; a
                                    placeholder here would be worse than no line at all.
                                    QA BUG-35 — on an e-mail page the line is there from entry: the campaign's id from the
                                    URL (it IS this page's campaign, never a guess), and its name once one of the sources
                                    above has it — «קמפיין: <שם> (#id)», each part an isolated run (<bdi>). On a "both"
                                    page, the WhatsApp unit's template too, from its own card. The WhatsApp unit's own
                                    page (its id is not an e-mail campaign) keeps the old rule. */}
                                {!isWaPage && Number.isInteger(campaignId) && campaignId > 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                        {t('DataSources.send.campaignPrefix')}
                                        {' '}
                                        {campaignName ? <><bdi>{campaignName}</bdi>{' '}</> : null}
                                        <bdi dir="ltr">{campaignName ? `(#${campaignId})` : `#${campaignId}`}</bdi>
                                    </Typography>
                                ) : campaignName && (
                                    <Typography variant="body2" color="textSecondary">
                                        {t('DataSources.send.campaignLabel', { name: campaignName })}
                                    </Typography>
                                )}
                                {!isWaPage && unitMode === 'both' && waTemplateName && (
                                    <Typography variant="body2" color="textSecondary">
                                        {t('DataSources.send.waTemplatePrefix')}
                                        {' '}
                                        <bdi>{waTemplateName}</bdi>
                                    </Typography>
                                )}
                            </Box>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {/* Unsaved changes now surface in a sticky save bar at the bottom of the
                                    screen (next to the Save action), not as a caption here. Per the
                                    product-owner reversal the screen DOES guard exits: a beforeunload
                                    handler (above) for tab close/refresh, and a three-way confirm dialog on
                                    the back-to-picker link (below). In-SPA side-menu navigation still cannot
                                    be intercepted (react-router 6.4.2 has no useBlocker) — the sticky bar is
                                    the mitigation for that path.
                                    The picker is this screen's entry point, so the way back to it must exist
                                    on the NORMAL path too — not only in the 404 branch. Rendered in the header
                                    (outside renderBody) so it is also reachable while the mapping is still
                                    loading or has failed. `Redirect` requires `openNewTab`.
                                    The ?dataSourceId= the screen was entered with is handed BACK: without it
                                    the picker loses the "sending from source" context the user started in and
                                    a second continue would drop the source entirely. The sanitised
                                    `parsedDsId` is used, not the raw param, so garbage is not round-tripped;
                                    it is a positive number, so it needs no encoding (contrast
                                    SmartSendPicker.tsx:64, which forwards the raw string). */}
                                <Button size="small" color="primary" onClick={handleBackToPicker}>
                                    {t('DataSources.send.backToPicker')}
                                </Button>
                            </Box>
                        </Box>
                        {/* QA BUG-35 (B) — a "both" send says so above everything, with its way out (see `bannerRemove`). */}
                        {bannerRemove && (
                            <InlineBanner
                                severity="warning"
                                role="status"
                                title={t(waAvailabilityKnownOff ? 'DataSources.send.bothBanner.titleWaOff' : 'DataSources.send.bothBanner.title')}
                                body={t(waAvailabilityKnownOff ? 'DataSources.send.bothBanner.bodyWaOff' : 'DataSources.send.bothBanner.body')}
                                action={(
                                    <span title={bannerRemove.disabled && bannerRemove.reason ? bannerRemove.reason : undefined}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            disabled={bannerRemove.disabled}
                                            onClick={bannerRemove.onRemove}
                                        >
                                            {t('DataSources.send.bothBanner.remove')}
                                        </Button>
                                    </span>
                                )}
                            />
                        )}
                        {renderBody()}
                    </Box>
                    {pagePreview && (
                        <Box style={{
                            flex: '5 1 340px', minWidth: 0,
                            ...(pinPreview ? { position: 'sticky' as const, top: 16, alignSelf: 'flex-start' as const } : {}),
                        }}>
                            <WhatsAppPhonePreview campaignId={waUnitId as number} classes={classes} />
                        </Box>
                    )}
                </Box>

                {/* item 3: sticky save bar — shown only when there are unsaved mapping changes.
                    Background + border mirror InlineBanner's `warning` severity; the text is
                    DARKENED to #8a5a00 (not InlineBanner's #b7791f) for WCAG-AA contrast — keep it
                    dark. It hosts the Save action so the unsaved indicator and the way to resolve
                    it sit together (replacing the old header caption that sat far from the Save
                    button). Sticky (not fixed) so it keeps its place in the RTL content column and
                    never overlaps the side menu. */}
                {/* Tabs mode: one sticky bar carries Preview / test send / send-to-all for BOTH
                    tabs, plus each channel's autosave state. With the add-on off it is not
                    rendered and the original in-body row above is used instead.

                    🔴 R1-05 · 2026-09-20 — AND IT IS GATED ON THE READ, NOT ONLY ON THE COLUMNS.
                    This bar is a SIBLING of renderBody() (:1145), so every early return in
                    there — the LinearProgress at :787 and the error branch at :788 — leaves it
                    standing. `hasColumns`, `canSend` and `saved` are all derived from a slice
                    that OUTLIVES a campaign-to-campaign navigation (`clearSmartSend` has zero
                    dispatch sites, :97), so on A→B the bar rendered campaign A's state with a
                    live "send to all" over B's id: POST SetMapping would carry B's CampaignID
                    with A's source, version and token map, and B's mapping is gone with no undo.

                    ⚠️ The predicate is renderBody's OWN (:786-788), restore INCLUDED. A bare
                    `mappingStatus === 'succeeded'` would be exactly the inverse of the two
                    load-bearing `!restoreOverlay` guards: it would take the bar away while a
                    restore is up, and after a FAILED restore it would never come back — and
                    with `waActive` this bar is the ONLY send / preview / test-send control,
                    because the in-body row at :998 renders only when `!waActive`. */}
                {waActive && hasColumns
                    && (restoring || restoreFailed || smartSend.mappingStatus === 'succeeded')
                    && !entryPending && (
                    <ActionBar
                        activeTab={effectiveTab}
                        canSend={canSend}
                        saving={saving}
                        emailSaveState={shownSaveState}
                        /* The E-MAIL HTML preview: only while the e-mail tab is the one on screen
                           (owner, stage 2026-09-28). The WhatsApp tab, and the WhatsApp unit's own
                           page (`effectiveTab` is 'whatsapp' there), preview in the phone. */
                        onPreview={effectiveTab === 'email'
                            ? () => setShowPreview((v) => !v)
                            : undefined}
                        onTestSend={() => (effectiveTab === 'whatsapp' ? setWaTestOpen(true) : setTestOpen(true))}
                        onSendToAll={requestSummary}
                        /* 12-fix-wave 7.4(g) — the same "closed" test as «החלף מקור» (7.4(d)). */
                        waTestClosed={sendIsClosed}
                    />
                )}

                {/* The e-mail save bar is redundant in tabs mode — the ActionBar carries both
                    channels' state — and two bars stacked at the bottom of one screen is how an
                    operator stops reading either. */}
                {!waActive && saveState !== 'idle' && (
                    <Box style={{
                        position: 'sticky', bottom: 0, marginTop: 20, zIndex: 2,
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        padding: '10px 16px', borderRadius: 6,
                        background: (saveState === 'error' || saveState === 'locked') ? '#fdecea' : (savedButDirty ? '#f5f5f5' : '#f1f8e9'),
                        border: `1px solid ${(saveState === 'error' || saveState === 'locked') ? '#f5c6cb' : (savedButDirty ? '#e0e0e0' : '#c5e1a5')}`,
                        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
                    }} aria-live="polite">
                        {saveState === 'saving' && (
                            <>
                                <CircularProgress size={16} />
                                <Typography variant="body2" style={{ color: 'var(--pls-d-text-2, #5b6b7b)' }}>{t('DataSources.send.autosave.saving')}</Typography>
                            </>
                        )}
                        {shownSaveState === 'saved' && (
                            <Typography variant="body2" style={{ color: 'var(--pls-d-success, #33691e)', fontWeight: 600 }}>{`✓ ${t('DataSources.send.autosave.saved')}`}</Typography>
                        )}
                        {savedButDirty && (
                            <Typography variant="body2" style={{ color: 'var(--pls-d-text-2, #5b6b7b)' }}>{t('DataSources.send.saveBar.unsaved')}</Typography>
                        )}
                        {saveState === 'locked' && (
                            <Typography variant="body2" style={{ color: 'var(--pls-d-danger, #8a1c1c)', fontWeight: 600 }}>
                                {/* 12-fix-wave 7.10 — a send that went out (409 SEND_ALREADY_SENT) is closed, not "in progress". */}
                                {t(closedHold ? 'DataSources.send.errors.sendClosed' : 'DataSources.send.errors.editBlockedDuringSend')}
                            </Typography>
                        )}
                        {saveState === 'error' && (
                            <>
                                <Typography variant="body2" style={{ color: 'var(--pls-d-danger, #8a1c1c)', fontWeight: 600 }}>{t('DataSources.send.autosave.saveFailed')}</Typography>
                                {/* Retry ONLY for the source the failed save belonged to. runAutoSave
                                    rebuilds the request from FRESH state, so after a source switch
                                    this button would post the new source's snapshot while presenting
                                    itself as retrying the old one. The failure is still reported —
                                    it must be, because the same switch cleared `dirty` and disarmed
                                    beforeunload, leaving this bar as the only evidence that those
                                    mappings were never persisted. */}
                                {failedSaveSourceId === currentSourceId ? (
                                    <Button variant="outlined" color="primary" size="small" startIcon={<Refresh />} onClick={() => runAutoSave()}>
                                        {t('DataSources.send.errors.retryAction')}
                                    </Button>
                                ) : (
                                    <Typography variant="body2" style={{ color: 'var(--pls-d-danger, #8a1c1c)' }}>
                                        {t('DataSources.send.source.saveFailedOtherSource')}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {/* onSent fires only on a 200/201 from sendSmart, on the line right after the dialog
                sets its own phase to 'result' (SendSummaryDialog.tsx:69-70). It must therefore do
                NOTHING that unmounts or navigates: React 17 renders both updates before the
                browser ever paints, so closing the dialog here made the dialog's success banner
                literally impossible to see — a completed send gave the user zero confirmation.
                It only records the outcome now; the dialog stays open on its result phase and
                renders the banner itself.
                Navigation is moved onto the close of a SUCCESSFUL send (every exit the dialog
                offers — its Close button, the X and the backdrop — comes through onClose), which
                still keeps the user off a screen whose "Send to all" would otherwise be re-armed.
                `canSend` also gates on `sent`, so the second send is blocked even if the redirect
                does not happen. */}
            <SendSummaryDialog
                open={summaryOpen}
                campaignId={campaignId}
                appClasses={classes}
                beforeSend={() => attachGroup(pendingGid)}
                onClose={() => {
                    setSummaryOpen(false);
                    if (sent) Redirect({ url: `${sitePrefix}Campaigns`, openNewTab: false });
                }}
                onSent={() => setSent(true)}
                onGoToSupervisor={() => setActiveTab('email')}
            />
            <TestSendDialog open={testOpen} campaignId={campaignId} dirty={dirty} onSaveMapping={saveMapping} onClose={() => setTestOpen(false)} onToast={(r) => showToast(r.ok, r.msg)} />

            {/* A SEPARATE dialog, not a branch inside `TestSendDialog`: that file is declared
                byte-identical by the contract (§1), and a WhatsApp test send is a different
                request with a different body, a different validation and a real per-message
                charge. */}
            {waActive && waUnitId != null && (
                <WhatsAppTestSendDialog
                    open={waTestOpen}
                    campaignId={waUnitId}
                    onClose={() => setWaTestOpen(false)}
                    autosave={waAutosave}
                />
            )}

            {/* item 3: three-way exit confirm. Save & exit is primary + autofocus; Exit without
                saving is muted and set apart (destructive — up to ~20 mappings); Esc → Cancel via
                onClose. A failed save keeps the dialog open and does NOT navigate. */}
            {/* dir is required: MUI v4 Dialogs portal into document.body, outside the
                <div dir={isRTL...}> at App.js:1018, and <html dir> is permanently "ltr" because
                App.js:727-730 sets it once at mount from i18n.language, which is still the 'en'
                default from i18n.js:20 at that moment (the real language lands later, App.js:806). */}
            <Dialog open={exitOpen} onClose={() => { if (!leavingRef.current) setExitOpen(false); }} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Typography variant="h6">{t('DataSources.send.exit.title')}</Typography>
                </Box>
                <Box style={{ padding: 24 }}>
                    <Typography variant="body2" color="textSecondary">{t('DataSources.send.exit.body')}</Typography>
                    {exitSaveFailed && (
                        <Typography variant="body2" style={{ color: 'var(--pls-d-danger, #c0392b)', marginTop: 12 }}>
                            {t('DataSources.send.exit.saveFailed')}
                        </Typography>
                    )}
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    {/* Invariant 24(3): with the tab locked, "save & exit" would promise a save
                        that `saveMapping` deliberately refuses to make. Only "leave without
                        saving" is offered there.
                        🔴 `|| waNeedsSave` — `emailLocked` ALONE WOULD HIDE THE BUTTON OVER
                        SAVEABLE WHATSAPP WORK. `selectEmailLocked` (selectors.ts:358-363) is
                        `waActive ∧ HasLeg ∧ EmailCampaignStatus ∈ EMAIL_SENT_STATUSES` — the mail
                        is out. The WhatsApp map is NOT locked by that: `canWrite`
                        (useWaAutosave.ts:60) refuses only while `EmailCampaignStatus = 2`, so in
                        the settled state the map is still editable and `waDirty` can be true.
                        Without this clause the dialog that the widened gate just opened would
                        offer "leave without saving" as its only action over work that could have
                        been saved. `waNeedsSave` already carries `canWrite`, so this cannot
                        re-introduce a button whose save would be refused. */}
                    {(!emailLocked || waNeedsSave) && (
                        <Button variant="contained" color="primary" disabled={saving} onClick={handleSaveAndExit} autoFocus>
                            {saving ? <CircularProgress size={18} color="inherit" /> : t('DataSources.send.exit.saveAndExit')}
                        </Button>
                    )}
                    <Box style={{ flex: 1 }} />
                    {/* QA BUG-10 — discard: drop the armed saves, and keep the dialog (= the hold) up
                        until `doExit` is done, so a POST Leave on the wire cannot let a fresh timer fire. */}
                    <Button variant="text" disabled={saving} onClick={async () => { if (leavingRef.current) return; stopAutosaveTimers(); await doExit(); if (mountedRef.current) setExitOpen(false); }} style={{ color: 'var(--pls-d-text-2, #8a8a8a)' }}>
                        {t('DataSources.send.exit.exitWithoutSaving')}
                    </Button>
                    <Button onClick={() => { if (!leavingRef.current) setExitOpen(false); }} disabled={saving}>{t('DataSources.send.cancel')}</Button>
                </Box>
            </Dialog>

            {/* 12-fix-wave 7.4(d) / 7.10 — the reset dialog: at «החלף מקור», and once after a save
                answered 409 SOURCE_RESET_CONFIRM_REQUIRED. No autoFocus on the destructive button.
                Esc / backdrop = «ביטול». Same skeleton and mandatory `dir` as the dialogs around it. */}
            {/* QA BUG-29 — mode 'entry' (the URL's source against the send's row) names both sources, in
                Idan's words, with «המשך» / «ביטול»; the other two modes are unchanged. */}
            <Dialog open={resetDlg != null} onClose={() => { cancelResetDialog(); }} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                {shownResetModeRef.current === 'entry' && (
                    <Box style={{ padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                        <Typography variant="h6">{t('DataSources.send.source.switchConfirmTitle')}</Typography>
                    </Box>
                )}
                <Box style={{ padding: 24 }}>
                    <Typography variant="body1">
                        {shownResetModeRef.current === 'entry'
                            ? t('DataSources.send.source.entryResetBody', { from: entryInfo?.from ?? '', to: entryInfo?.to ?? '' })
                            : t('DataSources.send.errors.sourceResetConfirmRequired')}
                    </Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Button variant="contained" color="primary" onClick={() => { confirmResetDialog(); }}>
                        {shownResetModeRef.current === 'entry'
                            ? t('DataSources.send.source.entryResetContinue')
                            : t('DataSources.send.source.resetConfirmAction')}
                    </Button>
                    <Box style={{ flex: 1 }} />
                    <Button onClick={() => { cancelResetDialog(); }}>{t('DataSources.send.cancel')}</Button>
                </Box>
            </Dialog>

            {/* The send-replaces-the-saved-mapping confirm. Reached only from requestSummary, i.e.
                only when the restore banner is on screen (a saved mapping exists and the table is
                NOT showing it) AND nothing has been mapped under the new source — the one shape in
                which pressing "שלח לכולם" silently trades a real mapping for an empty one. Same
                skeleton as the exit dialog above, including the mandatory `dir`: MUI v4 portals to
                document.body, outside App's <div dir>, and <html dir> is stuck at "ltr". */}
            <Dialog open={replaceConfirmOpen} onClose={() => setReplaceConfirmOpen(false)} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Typography variant="h6">{t('DataSources.send.source.sendReplaceTitle')}</Typography>
                </Box>
                <Box style={{ padding: 24 }}>
                    <Typography variant="body2" color="textSecondary">
                        {t('DataSources.send.source.sendReplaceBody', {
                            to: smartSend.dataSource?.Name ?? '',
                            from: saved?.dataSourceName ?? '',
                            mapped: (saved?.mappedTokenCount ?? 0).toLocaleString(),
                        })}
                    </Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    {/* No autoFocus: the destructive button is a deliberate second click, the same
                        rule the source-switch confirm follows. */}
                    <Button variant="contained" color="primary" disabled={saving}
                        onClick={() => { setReplaceConfirmOpen(false); openSummary(); }}>
                        {t('DataSources.send.actions.sendToAll')}
                    </Button>
                    <Box style={{ flex: 1 }} />
                    <Button onClick={() => setReplaceConfirmOpen(false)} disabled={saving}>{t('DataSources.send.cancel')}</Button>
                </Box>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast({ ...toast, open: false })}
                message={toast.msg}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                ContentProps={{ style: { backgroundColor: toast.ok ? '#2e7d32' : '#c0392b' } }}
            />
        </DefaultScreen>
    );
};

// ══ 09-channel-units — ONE INSTANCE PER SET OF UNITS ════════════════════════════════════════════
// The route keeps this component mounted across an in-app navigation to another `:id` or query, and
// with it every piece of local state: open dialogs, `dirty`, the save state, the send lock, armed
// autosave timers. Moving between units — the e-mail switch to the WhatsApp unit's page, removing
// the WhatsApp unit back to the e-mail page — must start the next page from its URL alone, so the
// page is keyed on the three things that name its units. Still an in-app navigation, so a save that
// is in flight completes. The store half of the same rule is `setWaUnit` (unit-scoped state).
const SmartSendScreen = (props: any) => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    // 12-fix-wave 7.4(a) — `wa` is no longer part of the key: the page's WhatsApp comes from its row (D5).
    const unitsKey = `${id ?? ''}|${searchParams.get('channel') ?? ''}`;
    return <SmartSendUnitsPage key={unitsKey} {...props} />;
};

export default SmartSendScreen;
